//  ---------------------------------------------------------------------------

import { keccak_256 as keccak } from '@noble/hashes/sha3.js';
import { secp256k1 } from '@noble/curves/secp256k1.js';
import Exchange from './abstract/aster.js';
import { AccountNotEnabled, AccountSuspended, ArgumentsRequired, AuthenticationError, BadRequest, BadResponse, BadSymbol, DuplicateOrderId, ExchangeClosedByUser, ExchangeError, InsufficientFunds, InvalidNonce, InvalidOrder, MarketClosed, NetworkError, NoChange, NotSupported, OperationFailed, OperationRejected, OrderImmediatelyFillable, OrderNotFillable, OrderNotFound, PermissionDenied, RateLimitExceeded, RequestTimeout, NullResponse } from './base/errors.js';
import { TRUNCATE, TICK_SIZE } from './base/functions/number.js';
import Precise from './base/Precise.js';
import type { Balances, Bool, Currencies, Currency, CurrencyInterface, Dict, FundingRate, FundingRates, int, Int, LastPrices, LedgerEntry, Leverage, Leverages, List, MarginMode, MarginModes, MarginModification, Market, NullableDict, Num, OHLCV, Order, OrderBook, OrderRequest, OrderSide, OrderType, Position, Str, Strings, SubType, Ticker, Tickers, Trade, TradingFeeInterface, Transaction, TransferEntry, PositionModeInfo, Endpoint } from './base/types.js';
import { ecdsa } from './base/functions/crypto.js';

//  ---------------------------------------------------------------------------xs
/**
 * @class aster
 * @augments Exchange
 */
export default class aster extends Exchange {
    override describe (): any {
        return this.deepExtend (super.describe (), {
            'id': 'aster',
            'name': 'Aster',
            'countries': [ 'US' ],
            // 3 req/s for free
            // 150 req/s for subscribers: https://aster.markets/data
            // for brokers: https://aster.markets/docs/api-references/broker-api/#authentication-and-rate-limit
            'rateLimit': 333,
            'certified': false,
            'pro': true,
            'dex': true,
            'urls': {
                'logo': 'https://github.com/user-attachments/assets/5e5909d6-c4de-4435-992f-4339c80edbd7',
                'www': 'https://www.asterdex.com/en',
                'api': {
                    'fapiPublic': 'https://fapi.asterdex.com/fapi',
                    'fapiPrivate': 'https://fapi.asterdex.com/fapi',
                    'sapiPublic': 'https://sapi.asterdex.com/api',
                    'sapiPrivate': 'https://sapi.asterdex.com/api',
                },
                'doc': 'https://github.com/asterdex/api-docs',
                'fees': 'https://docs.asterdex.com/product/asterex-simple/fees-and-slippage',
                'referral': {
                    'url': 'https://www.asterdex.com/en/referral/aA1c2B',
                    'discount': 0.1,
                },
            },
            'has': {
                'CORS': undefined,
                'spot': true,
                'margin': false,
                'swap': true,
                'future': false,
                'option': false,
                'addMargin': true,
                'borrowCrossMargin': false,
                'borrowIsolatedMargin': false,
                'cancelAllOrders': true,
                'cancelOrder': true,
                'cancelOrders': true,
                'closeAllPositions': false,
                'closePosition': false,
                'createConvertTrade': false,
                'createDepositAddress': false,
                'createLimitBuyOrder': false,
                'createLimitSellOrder': false,
                'createMarketBuyOrder': false,
                'createMarketBuyOrderWithCost': false,
                'createMarketOrderWithCost': false,
                'createMarketSellOrder': false,
                'createMarketSellOrderWithCost': false,
                'createOrder': true,
                'createOrders': true,
                'createOrderWithTakeProfitAndStopLoss': false,
                'createPostOnlyOrder': false,
                'createReduceOnlyOrder': false,
                'createStopLimitOrder': false,
                'createStopLossOrder': false,
                'createStopMarketOrder': false,
                'createStopOrder': false,
                'createTakeProfitOrder': false,
                'createTrailingPercentOrder': false,
                'createTriggerOrder': false,
                'editOrder': false,
                'editOrders': false,
                'fetchAccounts': undefined,
                'fetchBalance': true,
                'fetchBidsAsks': true,
                'fetchBorrowInterest': false,
                'fetchBorrowRateHistories': false,
                'fetchBorrowRateHistory': false,
                'fetchCanceledAndClosedOrders': 'emulated',
                'fetchCanceledOrders': 'emulated',
                'fetchClosedOrder': false,
                'fetchClosedOrders': 'emulated',
                'fetchConvertCurrencies': false,
                'fetchConvertQuote': false,
                'fetchConvertTrade': false,
                'fetchConvertTradeHistory': false,
                'fetchCrossBorrowRate': false,
                'fetchCrossBorrowRates': false,
                'fetchCurrencies': true,
                'fetchDeposit': false,
                'fetchDepositAddress': false,
                'fetchDepositAddresses': false,
                'fetchDepositAddressesByNetwork': false,
                'fetchDeposits': false,
                'fetchDepositsWithdrawals': false,
                'fetchDepositWithdrawFee': 'emulated',
                'fetchDepositWithdrawFees': false,
                'fetchFundingHistory': true,
                'fetchFundingInterval': 'emulated',
                'fetchFundingIntervals': true,
                'fetchFundingRate': true,
                'fetchFundingRateHistory': true,
                'fetchFundingRates': true,
                'fetchGreeks': false,
                'fetchIndexOHLCV': false,
                'fetchIsolatedBorrowRate': 'emulated',
                'fetchIsolatedBorrowRates': false,
                'fetchL3OrderBook': false,
                'fetchLastPrices': true,
                'fetchLedger': true,
                'fetchLedgerEntry': false,
                'fetchLeverage': 'emulated',
                'fetchLeverages': true,
                'fetchLeverageTiers': false,
                'fetchLiquidations': false,
                'fetchLongShortRatio': false,
                'fetchLongShortRatioHistory': false,
                'fetchMarginAdjustmentHistory': true,
                'fetchMarginMode': 'emulated',
                'fetchMarginModes': true,
                'fetchMarketLeverageTiers': 'emulated',
                'fetchMarkets': true,
                'fetchMarkOHLCV': false,
                'fetchMarkPrice': false,
                'fetchMarkPrices': false,
                'fetchMyLiquidations': false,
                'fetchMySettlementHistory': false,
                'fetchMyTrades': true,
                'fetchOHLCV': true,
                'fetchOpenInterest': false,
                'fetchOpenInterestHistory': false,
                'fetchOpenOrder': true,
                'fetchOpenOrders': true,
                'fetchOption': false,
                'fetchOptionChain': false,
                'fetchOrder': true,
                'fetchOrderBook': true,
                'fetchOrderBooks': false,
                'fetchOrders': true,
                'fetchOrderTrades': false,
                'fetchPosition': false,
                'fetchPositionHistory': false,
                'fetchPositionMode': true,
                'fetchPositions': true,
                'fetchPositionsHistory': false,
                'fetchPositionsRisk': true,
                'fetchPremiumIndexOHLCV': false,
                'fetchSettlementHistory': false,
                'fetchStatus': false,
                'fetchTicker': true,
                'fetchTickers': true,
                'fetchTime': true,
                'fetchTrades': true,
                'fetchTradingFee': true,
                'fetchTradingFees': false,
                'fetchTradingLimits': 'emulated',
                'fetchTransactionFee': 'emulated',
                'fetchTransactionFees': false,
                'fetchTransactions': false,
                'fetchTransfer': false,
                'fetchTransfers': false,
                'fetchUnderlyingAssets': false,
                'fetchVolatilityHistory': false,
                'fetchWithdrawAddresses': false,
                'fetchWithdrawal': false,
                'fetchWithdrawals': false,
                'fetchWithdrawalWhitelist': false,
                'reduceMargin': true,
                'repayCrossMargin': false,
                'repayIsolatedMargin': false,
                'sandbox': false,
                'setLeverage': true,
                'setMargin': false,
                'setMarginMode': true,
                'setPositionMode': true,
                'signIn': true,
                'transfer': true,
                'withdraw': true,
            },
            'api': {
                'fapiPublic': {
                    'get': {
                        'v1/ping': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/ping': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/time': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/time': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/exchangeInfo': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/exchangeInfo': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/depth': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/depth': { 'cost': 2 } as Endpoint<Dict>, // dynamic: 5, 10, 20, 50->2, 100->5, 500->10, 1000->20
                        'v1/trades': { 'cost': 1 } as Endpoint<List>,
                        'v3/trades': { 'cost': 1 } as Endpoint<List>,
                        'v1/historicalTrades': { 'cost': 1 } as Endpoint<List>,
                        'v3/historicalTrades': { 'cost': 20 } as Endpoint<List>,
                        'v1/aggTrades': { 'cost': 1 } as Endpoint<List>,
                        'v3/aggTrades': { 'cost': 20 } as Endpoint<List>,
                        'v1/klines': { 'cost': 1 } as Endpoint<List>,
                        'v3/klines': { 'cost': 1 } as Endpoint<List>, // dynamic [1,100) ->1,  [100, 500)->2, [500, 1000]->5, [1000 -> 10
                        'v1/indexPriceKlines': { 'cost': 1 } as Endpoint<List>,
                        'v3/indexPriceKlines': { 'cost': 1 } as Endpoint<List>, // same as klines
                        'v1/markPriceKlines': { 'cost': 1 } as Endpoint<List>,
                        'v3/markPriceKlines': { 'cost': 1 } as Endpoint<List>, // same as klines
                        'v1/premiumIndex': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/premiumIndex': { 'cost': 1 } as Endpoint<Dict | List>,
                        'v1/fundingRate': { 'cost': 1 } as Endpoint<List>,
                        'v3/fundingRate': { 'cost': 1 } as Endpoint<List>,
                        'v1/fundingInfo': { 'cost': 1 } as Endpoint<List>,
                        'v3/fundingInfo': { 'cost': 1 } as Endpoint<List>,
                        'v1/ticker/24hr': { 'cost': 1 } as Endpoint<List>,
                        'v3/ticker/24hr': { 'cost': 1 } as Endpoint<Dict | List>, // 1 single-symbol, otherwise 40
                        'v1/ticker/price': { 'cost': 1 } as Endpoint<List>,
                        'v3/ticker/price': { 'cost': 1 } as Endpoint<List>, // 1 single-symbol, otherwise 2
                        'v1/ticker/bookTicker': { 'cost': 1 } as Endpoint<List>,
                        'v3/ticker/bookTicker': { 'cost': 1 } as Endpoint<List>, // 1 single-symbol, otherwise 2
                        // different endpoints
                        'v1/adlQuantile': { 'cost': 1 } as Endpoint<List>,
                        'v1/forceOrders': { 'cost': 1 } as Endpoint<List>,
                        'v3/indexreferences': { 'cost': 1 } as Endpoint<Dict>,
                    },
                },
                'fapiPrivate': {
                    'get': {
                        'v1/positionSide/dual': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/positionSide/dual': { 'cost': 30 } as Endpoint<Dict>,
                        'v1/multiAssetsMargin': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/multiAssetsMargin': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/order': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/order': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/openOrder': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/openOrder': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/openOrders': { 'cost': 1 } as Endpoint<List>,
                        'v3/openOrders': { 'cost': 1 } as Endpoint<List>,
                        'v1/allOrders': { 'cost': 1 } as Endpoint<List>,
                        'v3/allOrders': { 'cost': 1 } as Endpoint<List>,
                        'v2/balance': { 'cost': 1 } as Endpoint<List>,
                        'v3/balance': { 'cost': 1 } as Endpoint<List>,
                        'v3/account': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/positionMargin/history': { 'cost': 1 } as Endpoint<List>,
                        'v3/positionMargin/history': { 'cost': 1 } as Endpoint<List>,
                        'v2/positionRisk': { 'cost': 1 } as Endpoint<List>,
                        'v3/positionRisk': { 'cost': 1 } as Endpoint<List>,
                        'v1/userTrades': { 'cost': 1 } as Endpoint<List>,
                        'v3/userTrades': { 'cost': 5 } as Endpoint<List>,
                        'v1/income': { 'cost': 1 } as Endpoint<List>,
                        'v3/income': { 'cost': 1 } as Endpoint<List>,
                        'v1/leverageBracket': { 'cost': 1 } as Endpoint<List>,
                        'v3/leverageBracket': { 'cost': 1 } as Endpoint<List>,
                        'v1/commissionRate': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/commissionRate': { 'cost': 1 } as Endpoint<Dict>,
                        // others
                        'v3/adlQuantile': { 'cost': 1 } as Endpoint<List>,
                        'v3/forceOrders': { 'cost': 1 } as Endpoint<List>,
                        'v3/mmp': { 'cost': 1 } as Endpoint<List>,
                        'v3/accountWithJoinMargin': { 'cost': 1 } as Endpoint<Dict>,
                        'v4/account': { 'cost': 1 } as Endpoint<Dict>,
                        // builder
                        'v3/agent': { 'cost': 1 } as Endpoint<List>,
                        'v3/builder': { 'cost': 1 } as Endpoint<List>,
                    },
                    'post': {
                        'v1/positionSide/dual': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/positionSide/dual': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/multiAssetsMargin': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/multiAssetsMargin': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/order': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/order': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/order/test': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/order/test': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/batchOrders': { 'cost': 1 } as Endpoint<List>,
                        'v3/batchOrders': { 'cost': 1 } as Endpoint<List>,
                        'v1/asset/wallet/transfer': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/asset/wallet/transfer': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/countdownCancelAll': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/countdownCancelAll': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/leverage': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/leverage': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/marginType': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/marginType': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/positionMargin': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/positionMargin': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/listenKey': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/listenKey': { 'cost': 1 } as Endpoint<Dict>,
                        // others
                        'v3/mmp': { 'cost': 1 } as Endpoint<List>,
                        'v3/mmpReset': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/noop': { 'cost': 1 } as Endpoint<Dict>,
                        // builder
                        'v3/approveAgent': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/updateAgent': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/approveBuilder': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/updateBuilder': { 'cost': 1 } as Endpoint<Dict>,
                    },
                    'put': {
                        'v1/listenKey': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/listenKey': { 'cost': 1 } as Endpoint<Dict>,
                    },
                    'delete': {
                        'v1/order': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/order': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/allOpenOrders': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/allOpenOrders': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/batchOrders': { 'cost': 1 } as Endpoint<List>,
                        'v3/batchOrders': { 'cost': 1 } as Endpoint<List>,
                        'v3/mmp': { 'cost': 1 } as Endpoint<List>,
                        'v1/listenKey': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/listenKey': { 'cost': 1 } as Endpoint<Dict>,
                        // builder
                        'v3/agent': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/builder': { 'cost': 1 } as Endpoint<Dict>,
                    },
                },
                'sapiPublic': {
                    'get': {
                        // v1
                        'v1/ping': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/time': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/exchangeInfo': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/depth': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/trades': { 'cost': 1 } as Endpoint<List>,
                        'v1/historicalTrades': { 'cost': 1 } as Endpoint<List>,
                        'v1/aggTrades': { 'cost': 1 } as Endpoint<List>,
                        'v1/klines': { 'cost': 1 } as Endpoint<List>,
                        'v1/ticker/24hr': { 'cost': 1 } as Endpoint<List>,
                        'v1/ticker/price': { 'cost': 1 } as Endpoint<List>,
                        'v1/ticker/bookTicker': { 'cost': 1 } as Endpoint<List>,
                        'v1/aster/withdraw/estimateFee': { 'cost': 1 } as Endpoint<Dict>,
                        // v3
                        'v3/ping': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/time': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/exchangeInfo': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/depth': { 'cost': 2, 'byLimit': [ [ 50, 2 ], [ 100, 5 ], [ 500, 10 ], [ 1000, 20 ] ] } as Endpoint<Dict>,
                        'v3/trades': { 'cost': 1 } as Endpoint<List>,
                        'v3/historicalTrades': { 'cost': 20 } as Endpoint<List>,
                        'v3/aggTrades': { 'cost': 20 } as Endpoint<List>,
                        'v3/klines': { 'cost': 1, 'byLimit': [ [ 99, 1 ], [ 499, 2 ], [ 1000, 5 ], [ 10000, 10 ] ] } as Endpoint<List>, // todo: not specified in docs
                        'v3/ticker/24hr': { 'cost': 1, 'noSymbol': 40 } as Endpoint<Dict | List>,
                        'v3/ticker/price': { 'cost': 1, 'noSymbol': 2 } as Endpoint<List>,
                        'v3/ticker/bookTicker': { 'cost': 1, 'noSymbol': 2 } as Endpoint<List>,
                        'v3/aster/withdraw/estimateFee': { 'cost': 1 } as Endpoint<Dict>,
                    },
                },
                'sapiPrivate': {
                    'get': {
                        // v1
                        'v1/commissionRate': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/order': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/openOrders': { 'cost': 1 } as Endpoint<List>,
                        'v1/allOrders': { 'cost': 1 } as Endpoint<List>,
                        'v1/transactionHistory': { 'cost': 1 } as Endpoint<List>,
                        'v1/account': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/userTrades': { 'cost': 1 } as Endpoint<List>,
                        // v3
                        'v3/commissionRate': { 'cost': 1, 'noSymbol': 2 } as Endpoint<Dict>,
                        'v3/order': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/openOrders': { 'cost': 1 } as Endpoint<List>, // with symbol 1, otherwise 40
                        'v3/allOrders': { 'cost': 5 } as Endpoint<List>,
                        'v3/account': { 'cost': 5 } as Endpoint<Dict>,
                        'v3/userTrades': { 'cost': 5 } as Endpoint<List>,
                        'v3/openOrder': { 'cost': 1 } as Endpoint<Dict>,
                    },
                    'post': {
                        // v1
                        'v1/order': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/asset/wallet/transfer': { 'cost': 5 } as Endpoint<Dict>,
                        'v1/asset/sendToAddress': { 'cost': 1 } as Endpoint<Dict>, // inexistent in v3
                        'v1/listenKey': { 'cost': 1 } as Endpoint<Dict>,
                        // v3
                        'v3/order': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/asset/wallet/transfer': { 'cost': 5 } as Endpoint<Dict>,
                        'v3/aster/user-withdraw': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/listenKey': { 'cost': 1 } as Endpoint<Dict>,
                    },
                    'put': {
                        'v1/listenKey': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/listenKey': { 'cost': 1 } as Endpoint<Dict>,
                    },
                    'delete': {
                        // v1
                        'v1/order': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/allOpenOrders': { 'cost': 1 } as Endpoint<Dict>,
                        'v1/listenKey': { 'cost': 1 } as Endpoint<Dict>,
                        // v3
                        'v3/allOpenOrders': { 'cost': 1 } as Endpoint<List>,
                        'v3/order': { 'cost': 1 } as Endpoint<Dict>,
                        'v3/listenKey': { 'cost': 1 } as Endpoint<Dict>,
                    },
                },
            },
            'timeframes': {
                '1m': '1m',
                '3m': '3m',
                '5m': '5m',
                '15m': '15m',
                '30m': '30m',
                '1h': '1h',
                '2h': '2h',
                '4h': '4h',
                '6h': '6h',
                '8h': '8h',
                '12h': '12h',
                '1d': '1d',
                '3d': '3d',
                '1w': '1w',
                '1M': '1M',
            },
            'precisionMode': TICK_SIZE,
            'requiredCredentials': {
                'apiKey': false,
                'secret': false,
                'privateKey': true,
            },
            'fees': {
                'trading': {
                    'tierBased': true,
                    'percentage': true,
                    'maker': this.parseNumber ('0.0001'),
                    'taker': this.parseNumber ('0.00035'),
                },
            },
            'features': {
                'spot': {
                    'sandbox': false,
                    'createOrder': {
                        'marginMode': false,
                        'triggerPrice': true,
                        'triggerPriceType': undefined,
                        'triggerDirection': undefined,
                        'stopLossPrice': true,
                        'takeProfitPrice': true,
                        'attachedStopLossTakeProfit': undefined,
                        'timeInForce': {
                            'IOC': true,
                            'FOK': true,
                            'PO': true,
                            'GTD': false,
                        },
                        'hedged': false,
                        'trailing': false,
                        'leverage': false,
                        'marketBuyByCost': true,
                        'marketBuyRequiresPrice': false,
                        'selfTradePrevention': false,
                        'iceberg': false,
                    },
                    'createOrders': undefined,
                    'fetchMyTrades': {
                        'marginMode': false,
                        'limit': 1000,
                        'daysBack': undefined,
                        'untilDays': undefined,
                        'symbolRequired': true,
                    },
                    'fetchOrder': {
                        'marginMode': false,
                        'trigger': false,
                        'trailing': false,
                        'symbolRequired': true,
                    },
                    'fetchOpenOrders': {
                        'marginMode': false,
                        'limit': undefined,
                        'trigger': false,
                        'trailing': false,
                        'symbolRequired': false,
                    },
                    'fetchOrders': {
                        'marginMode': false,
                        'limit': 1000,
                        'daysBack': undefined,
                        'untilDays': undefined,
                        'trigger': false,
                        'trailing': false,
                        'symbolRequired': true,
                    },
                    'fetchClosedOrders': undefined,
                    'fetchOHLCV': {
                        'limit': 1500,
                    },
                },
                'forDerivs': {
                    'sandbox': false,
                    'createOrder': {
                        'marginMode': false,
                        'triggerPrice': true,
                        'triggerPriceType': {
                            'last': true,
                            'mark': true,
                            'index': false,
                        },
                        'triggerDirection': false,
                        'stopLossPrice': true,
                        'takeProfitPrice': true,
                        'attachedStopLossTakeProfit': undefined,
                        'timeInForce': {
                            'IOC': true,
                            'FOK': true,
                            'PO': true,
                            'GTD': false,
                        },
                        'hedged': true,
                        'trailing': true,
                        'leverage': false,
                        'marketBuyByCost': false,
                        'marketBuyRequiresPrice': false,
                        'selfTradePrevention': false,
                        'iceberg': false,
                    },
                    'createOrders': undefined,
                    'fetchMyTrades': {
                        'marginMode': false,
                        'limit': 1000,
                        'daysBack': undefined,
                        'untilDays': undefined,
                        'symbolRequired': true,
                    },
                    'fetchOrder': {
                        'marginMode': false,
                        'trigger': false,
                        'trailing': false,
                        'symbolRequired': true,
                    },
                    'fetchOpenOrders': {
                        'marginMode': false,
                        'limit': undefined,
                        'trigger': false,
                        'trailing': false,
                        'symbolRequired': false,
                    },
                    'fetchOrders': {
                        'marginMode': false,
                        'limit': 1000,
                        'daysBack': undefined,
                        'untilDays': undefined,
                        'trigger': false,
                        'trailing': false,
                        'symbolRequired': true,
                    },
                    'fetchClosedOrders': undefined,
                    'fetchOHLCV': {
                        'limit': 1500,
                    },
                },
                'swap': {
                    'linear': {
                        'extends': 'forDerivs',
                    },
                    'inverse': undefined,
                },
            },
            'options': {
                'defaultType': 'spot',
                'recvWindow': 10 * 1000, // 10 sec
                'zeroAddress': '0x0000000000000000000000000000000000000000',
                'v3ChainId': 1666, // Aster chain ID used for EIP-712 v3 signing
                'createOrder': {
                    'timeInForce': 'GTC', // 'GTC' = Good To Cancel (default), 'IOC' = Immediate Or Cancel
                    'quoteOrderQty': true, // whether market orders support amounts in quote currency
                },
                'accountsByType': {
                    'spot': 'SPOT',
                    'swap': 'FUTURE',
                    'future': 'FUTURE',
                    'linear': 'FUTURE',
                },
                'networks': {
                    'ERC20': 'ETH',
                    'BEP20': 'BSC',
                    'ARBITRUM': 'Arbitrum',
                },
                'networksToChainId': {
                    'ETH': 1,
                    'BSC': 56,
                    'Arbitrum': 42161,
                },
                'fetchOpenOrders': {
                    'warnIfNoSymbol': true, // set to false to suppress warning when calling fetchOpenOrders without symbol
                },
                'builderFee': true,
                'builder': '0x1F5877C19e3777Cfd15F9d57253eA4aA5254Ec39',
                'builderRate': '0.001',
            },
            'exceptions': {
                'exact': {
                    // 10xx - General Server or Network issues
                    '-1000': OperationRejected, // UNKNOWN
                    '-1001': NetworkError, // DISCONNECTED
                    '-1002': AuthenticationError, // UNAUTHORIZED
                    '-1003': RateLimitExceeded, // TOO_MANY_REQUESTS
                    '-1004': DuplicateOrderId, // DUPLICATE_IP
                    '-1005': BadRequest, // NO_SUCH_IP
                    '-1006': BadResponse, // UNEXPECTED_RESP
                    '-1007': RequestTimeout, // TIMEOUT
                    '-1010': OperationRejected, // ERROR_MSG_RECEIVED
                    '-1011': PermissionDenied, // NON_WHITE_LIST
                    '-1013': BadRequest, // INVALID_MESSAGE
                    '-1014': OrderNotFillable, // UNKNOWN_ORDER_COMPOSITION
                    '-1015': RateLimitExceeded, // TOO_MANY_ORDERS
                    '-1016': ExchangeClosedByUser, // SERVICE_SHUTTING_DOWN
                    '-1020': NotSupported, // UNSUPPORTED_OPERATION
                    '-1021': InvalidNonce, // INVALID_TIMESTAMP
                    '-1022': AuthenticationError, // INVALID_SIGNATURE
                    '-1023': BadRequest, // START_TIME_GREATER_THAN_END_TIME
                    // 11xx - Request issues
                    '-1100': BadRequest, // ILLEGAL_CHARS
                    '-1101': BadRequest, // TOO_MANY_PARAMETERS
                    '-1102': ArgumentsRequired, // MANDATORY_PARAM_EMPTY_OR_MALFORMED
                    '-1103': BadRequest, // UNKNOWN_PARAM
                    '-1104': BadRequest, // UNREAD_PARAMETERS
                    '-1105': ArgumentsRequired, // PARAM_EMPTY
                    '-1106': BadRequest, // PARAM_NOT_REQUIRED
                    '-1108': BadRequest, // BAD_ASSET
                    '-1109': BadRequest, // BAD_ACCOUNT
                    '-1110': BadSymbol, // BAD_INSTRUMENT_TYPE
                    '-1111': BadRequest, // BAD_PRECISION
                    '-1112': BadRequest, // NO_DEPTH
                    '-1113': BadRequest, // WITHDRAW_NOT_NEGATIVE
                    '-1114': BadRequest, // TIF_NOT_REQUIRED
                    '-1115': InvalidOrder, // INVALID_TIF
                    '-1116': InvalidOrder, // INVALID_ORDER_TYPE
                    '-1117': InvalidOrder, // INVALID_SIDE
                    '-1118': InvalidOrder, // EMPTY_NEW_CL_ORD_ID
                    '-1119': InvalidOrder, // EMPTY_ORG_CL_ORD_ID
                    '-1120': BadRequest, // BAD_INTERVAL
                    '-1121': BadSymbol, // BAD_SYMBOL
                    '-1125': AuthenticationError, // INVALID_LISTEN_KEY
                    '-1127': BadRequest, // MORE_THAN_XX_HOURS
                    '-1128': BadRequest, // OPTIONAL_PARAMS_BAD_COMBO
                    '-1130': BadRequest, // INVALID_PARAMETER
                    '-1136': InvalidOrder, // INVALID_NEW_ORDER_RESP_TYPE
                    // 20xx - Processing Issues
                    '-2010': InvalidOrder, // NEW_ORDER_REJECTED
                    '-2011': OrderNotFound, // CANCEL_REJECTED
                    '-2013': OrderNotFound, // NO_SUCH_ORDER
                    '-2014': AuthenticationError, // BAD_API_KEY_FMT
                    '-2015': AuthenticationError, // REJECTED_MBX_KEY
                    '-2016': MarketClosed, // NO_TRADING_WINDOW
                    '-2018': InsufficientFunds, // BALANCE_NOT_SUFFICIENT
                    '-2019': InsufficientFunds, // MARGIN_NOT_SUFFICIEN
                    '-2020': OrderNotFillable, // UNABLE_TO_FILL
                    '-2021': OrderImmediatelyFillable, // ORDER_WOULD_IMMEDIATELY_TRIGGER
                    '-2022': OperationRejected, // REDUCE_ONLY_REJECT
                    '-2023': AccountSuspended, // USER_IN_LIQUIDATION
                    '-2024': InsufficientFunds, // POSITION_NOT_SUFFICIENT
                    '-2025': RateLimitExceeded, // MAX_OPEN_ORDER_EXCEEDED
                    '-2026': NotSupported, // REDUCE_ONLY_ORDER_TYPE_NOT_SUPPORTED
                    '-2027': BadRequest, // MAX_LEVERAGE_RATIO
                    '-2028': BadRequest, // MIN_LEVERAGE_RATIO
                    // 40xx - Filters and other Issues
                    '-4000': InvalidOrder, // INVALID_ORDER_STATUS
                    '-4001': InvalidOrder, // PRICE_LESS_THAN_ZERO
                    '-4002': InvalidOrder, // PRICE_GREATER_THAN_MAX_PRICE
                    '-4003': InvalidOrder, // QTY_LESS_THAN_ZERO
                    '-4004': InvalidOrder, // QTY_LESS_THAN_MIN_QTY
                    '-4005': InvalidOrder, // QTY_GREATER_THAN_MAX_QTY
                    '-4006': InvalidOrder, // STOP_PRICE_LESS_THAN_ZERO
                    '-4007': InvalidOrder, // STOP_PRICE_GREATER_THAN_MAX_PRICE
                    '-4008': InvalidOrder, // TICK_SIZE_LESS_THAN_ZERO
                    '-4009': InvalidOrder, // MAX_PRICE_LESS_THAN_MIN_PRICE
                    '-4010': InvalidOrder, // MAX_QTY_LESS_THAN_MIN_QTY
                    '-4011': InvalidOrder, // STEP_SIZE_LESS_THAN_ZERO
                    '-4012': RateLimitExceeded, // MAX_NUM_ORDERS_LESS_THAN_ZERO
                    '-4013': InvalidOrder, // PRICE_LESS_THAN_MIN_PRICE
                    '-4014': InvalidOrder, // PRICE_NOT_INCREASED_BY_TICK_SIZE
                    '-4015': InvalidOrder, // INVALID_CL_ORD_ID_LEN
                    '-4016': InvalidOrder, // PRICE_HIGHTER_THAN_MULTIPLIER_UP
                    '-4017': InvalidOrder, // MULTIPLIER_UP_LESS_THAN_ZERO
                    '-4018': InvalidOrder, // MULTIPLIER_DOWN_LESS_THAN_ZERO
                    '-4019': BadRequest, // COMPOSITE_SCALE_OVERFLOW
                    '-4020': BadRequest, // TARGET_STRATEGY_INVALID
                    '-4021': BadRequest, // INVALID_DEPTH_LIMIT
                    '-4022': MarketClosed, // WRONG_MARKET_STATUS
                    '-4023': InvalidOrder, // QTY_NOT_INCREASED_BY_STEP_SIZE
                    '-4024': InvalidOrder, // PRICE_LOWER_THAN_MULTIPLIER_DOWN
                    '-4025': BadRequest, // MULTIPLIER_DECIMAL_LESS_THAN_ZERO
                    '-4026': BadRequest, // COMMISSION_INVALID
                    '-4027': BadRequest, // INVALID_ACCOUNT_TYPE
                    '-4028': BadRequest, // INVALID_LEVERAGE
                    '-4029': BadRequest, // INVALID_TICK_SIZE_PRECISION
                    '-4030': BadRequest, // INVALID_STEP_SIZE_PRECISION
                    '-4031': BadRequest, // INVALID_WORKING_TYPE
                    '-4032': RateLimitExceeded, // EXCEED_MAX_CANCEL_ORDER_SIZE
                    '-4033': AccountNotEnabled, // INSURANCE_ACCOUNT_NOT_FOUND
                    '-4044': BadRequest, // INVALID_BALANCE_TYPE
                    '-4045': RateLimitExceeded, // MAX_STOP_ORDER_EXCEEDED
                    '-4046': NoChange, // NO_NEED_TO_CHANGE_MARGIN_TYPE
                    '-4047': OperationRejected, // THERE_EXISTS_OPEN_ORDERS
                    '-4048': OperationRejected, // THERE_EXISTS_QUANTITY
                    '-4049': OperationRejected, // ADD_ISOLATED_MARGIN_REJECT
                    '-4050': InsufficientFunds, // CROSS_BALANCE_INSUFFICIENT
                    '-4051': InsufficientFunds, // ISOLATED_BALANCE_INSUFFICIENT
                    '-4052': NoChange, // NO_NEED_TO_CHANGE_AUTO_ADD_MARGIN
                    '-4053': OperationRejected, // AUTO_ADD_CROSSED_MARGIN_REJECT
                    '-4054': OperationRejected, // ADD_ISOLATED_MARGIN_NO_POSITION_REJECT
                    '-4055': ArgumentsRequired, // AMOUNT_MUST_BE_POSITIVE
                    '-4056': AuthenticationError, // INVALID_API_KEY_TYPE
                    '-4057': AuthenticationError, // INVALID_RSA_PUBLIC_KEY
                    '-4058': InvalidOrder, // MAX_PRICE_TOO_LARGE
                    '-4059': NoChange, // NO_NEED_TO_CHANGE_POSITION_SIDE
                    '-4060': InvalidOrder, // INVALID_POSITION_SIDE
                    '-4061': InvalidOrder, // POSITION_SIDE_NOT_MATCH
                    '-4062': OperationRejected, // REDUCE_ONLY_CONFLICT
                    '-4063': BadRequest, // INVALID_OPTIONS_REQUEST_TYPE
                    '-4064': BadRequest, // INVALID_OPTIONS_TIME_FRAME
                    '-4065': BadRequest, // INVALID_OPTIONS_AMOUNT
                    '-4066': BadRequest, // INVALID_OPTIONS_EVENT_TYPE
                    '-4067': OperationRejected, // POSITION_SIDE_CHANGE_EXISTS_OPEN_ORDERS
                    '-4068': OperationRejected, // POSITION_SIDE_CHANGE_EXISTS_QUANTITY
                    '-4069': BadRequest, // INVALID_OPTIONS_PREMIUM_FEE
                    '-4070': InvalidOrder, // INVALID_CL_OPTIONS_ID_LEN
                    '-4071': InvalidOrder, // INVALID_OPTIONS_DIRECTION
                    '-4072': NoChange, // OPTIONS_PREMIUM_NOT_UPDATE
                    '-4073': BadRequest, // OPTIONS_PREMIUM_INPUT_LESS_THAN_ZERO
                    '-4074': InvalidOrder, // OPTIONS_AMOUNT_BIGGER_THAN_UPPER
                    '-4075': OperationRejected, // OPTIONS_PREMIUM_OUTPUT_ZERO
                    '-4076': OperationRejected, // OPTIONS_PREMIUM_TOO_DIFF
                    '-4077': RateLimitExceeded, // OPTIONS_PREMIUM_REACH_LIMIT
                    '-4078': BadRequest, // OPTIONS_COMMON_ERROR
                    '-4079': BadRequest, // INVALID_OPTIONS_ID
                    '-4080': BadRequest, // OPTIONS_USER_NOT_FOUND
                    '-4081': BadRequest, // OPTIONS_NOT_FOUND
                    '-4082': RateLimitExceeded, // INVALID_BATCH_PLACE_ORDER_SIZE
                    '-4083': OperationFailed, // PLACE_BATCH_ORDERS_FAIL
                    '-4084': NotSupported, // UPCOMING_METHOD
                    '-4085': BadRequest, // INVALID_NOTIONAL_LIMIT_COEF
                    '-4086': BadRequest, // INVALID_PRICE_SPREAD_THRESHOLD
                    '-4087': PermissionDenied, // REDUCE_ONLY_ORDER_PERMISSION
                    '-4088': PermissionDenied, // NO_PLACE_ORDER_PERMISSION
                    '-4104': BadSymbol, // INVALID_CONTRACT_TYPE
                    '-4114': InvalidOrder, // INVALID_CLIENT_TRAN_ID_LEN
                    '-4115': DuplicateOrderId, // DUPLICATED_CLIENT_TRAN_ID
                    '-4118': InsufficientFunds, // REDUCE_ONLY_MARGIN_CHECK_FAILED
                    '-4131': InvalidOrder, // MARKET_ORDER_REJECT
                    '-4135': InvalidOrder, // INVALID_ACTIVATION_PRICE
                    '-4137': InvalidOrder, // QUANTITY_EXISTS_WITH_CLOSE_POSITION
                    '-4138': OperationRejected, // REDUCE_ONLY_MUST_BE_TRUE
                    '-4139': InvalidOrder, // ORDER_TYPE_CANNOT_BE_MKT
                    '-4140': OperationRejected, // INVALID_OPENING_POSITION_STATUS
                    '-4141': MarketClosed, // SYMBOL_ALREADY_CLOSED
                    '-4142': InvalidOrder, // STRATEGY_INVALID_TRIGGER_PRICE
                    '-4144': BadSymbol, // INVALID_PAIR
                    '-4161': OperationRejected, // ISOLATED_LEVERAGE_REJECT_WITH_POSITION
                    '-4164': InvalidOrder, // MIN_NOTIONAL
                    '-4165': BadRequest, // INVALID_TIME_INTERVAL
                    '-4183': InvalidOrder, // PRICE_HIGHTER_THAN_STOP_MULTIPLIER_UP
                    '-4184': InvalidOrder, // PRICE_LOWER_THAN_STOP_MULTIPLIER_DOWN
                    '-5060': OperationRejected, // {"code":-5060,"msg":"The limit order price does not meet the PERCENT_PRICE filter limit."}
                    '-5076': OperationRejected, // {"code":-5076,"msg":"Total order value should be more than 5 USDT"}
                    // occured errors:
                    '-4168': OperationRejected, // Unable to adjust to isolated-margin mode under the Multi-Assets mode.
                },
                'broad': {
                },
            },
        });
    }

    isInverse (type: string, subType: Str = undefined): boolean {
        if (subType === undefined) {
            return (type === 'delivery');
        } else {
            return subType === 'inverse';
        }
    }

    isLinear (type: string, subType: Str = undefined): boolean {
        if (subType === undefined) {
            return (type === 'future') || (type === 'swap');
        } else {
            return subType === 'linear';
        }
    }

    /**
     * @method
     * @name aster#fetchCurrencies
     * @description fetches all available currencies on an exchange
     * @see https://asterdex.github.io/aster-api-website/spot-v3/market-data/#trading-specification-information
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#exchange-information
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} an associative dictionary of currencies
     */
    override async fetchCurrencies (params = {}): Promise<Currencies> {
        const sapiResult = await this.sapiPublicGetV3ExchangeInfo (params);
        const sapiRows = this.safeList (sapiResult, 'assets', []);
        //
        //     [
        //         {
        //             "asset": "USDT",
        //             "marginAvailable": true,           // only in PERP
        //             "autoAssetExchange": "-10000"      // only in PERP
        //         }
        //     ]
        //
        return this.parseCurrencies (sapiRows);
    }

    override parseCurrency (rawCurrency: Dict): CurrencyInterface {
        const currencyId = this.safeString (rawCurrency, 'asset');
        const code = this.safeCurrencyCode (currencyId);
        return this.safeCurrencyStructure ({
            'info': rawCurrency,
            'code': code,
            'id': currencyId,
            'name': code,
            'active': undefined,
            'deposit': undefined,
            'withdraw': undefined,
            'fee': undefined,
            'precision': undefined,
            'margin': this.safeBool (rawCurrency, 'marginAvailable'),
            'limits': {
                'amount': {
                    'min': undefined,
                    'max': undefined,
                },
                'withdraw': {
                    'min': undefined,
                    'max': undefined,
                },
                'deposit': {
                    'min': undefined,
                    'max': undefined,
                },
            },
            'networks': undefined,
            'type': 'crypto', // atm exchange api provides only cryptos
        });
    }

    /**
     * @method
     * @name aster#fetchMarkets
     * @description retrieves data on all markets for bigone
     * @see https://asterdex.github.io/aster-api-website/spot-v3/market-data/#trading-specification-information
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#exchange-information
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} an array of objects representing market data
     */
    override async fetchMarkets (params = {}): Promise<Market[]> {
        const promises: Promise<any>[] = [
            this.sapiPublicGetV3ExchangeInfo (params),
            this.fapiPublicGetV3ExchangeInfo (params),
        ];
        promises.push (this.signIn ());
        const results = await Promise.all (promises);
        const sapiResult = this.safeDict (results, 0, {});
        const sapiRows = this.safeList (sapiResult, 'symbols', []);
        const fapiResult = this.safeDict (results, 1, {});
        const fapiRows = this.safeList (fapiResult, 'symbols', []);
        //
        // example:
        //
        //     [
        //       {
        //         symbol: "TESTUSDT",
        //         status: "TRADING",
        //         baseAsset: "TEST",
        //         quoteAsset: "USDT",
        //         pricePrecision: "2",
        //         quantityPrecision: "5",
        //         baseAssetPrecision: "8",
        //         quotePrecision: "8",
        //         listingTime: "1756289680210",      // only in SPOT
        //         baseAssetAddress: null,            // only in SPOT
        //         ocoAllowed: false,                 // only in SPOT
        //         pair: "ASTERUSDT",                 // only in PERP
        //         contractType: "PERPETUAL",         // only in PERP
        //         deliveryDate: "4133404800000",     // only in PERP
        //         onboardDate: "1758178800000",      // only in PERP
        //         maintMarginPercent: "12.5000",     // only in PERP
        //         requiredMarginPercent: "25.0000",  // only in PERP
        //         marginAsset: "USDT",               // only in PERP
        //         underlyingType: "COIN",            // only in PERP
        //         underlyingSubType: [ "Top", ],     // only in PERP
        //         symbolType: "0",                   // only in PERP
        //         tradingMode: "0",                  // only in PERP
        //         name: "",                          // only in PERP
        //         channel: "{}",                     // only in PERP
        //         sequenceNo: "100",                 // only in PERP
        //         twapMinNotional: "1000",           // only in PERP
        //         imn: "4000.00",                    // only in PERP
        //         tags: [],                          // only in PERP
        //         settlePlan: "0",                   // only in PERP
        //         triggerProtect: "0.1500",          // only in PERP
        //         liquidationFee: "0.025000",        // only in PERP
        //         marketTakeBound: "0.05",           // only in PERP
        //         createTime: "1758215451058",       // only in PERP
        //         filters: [
        //           {
        //             minPrice: "0.01",
        //             maxPrice: "1000000",
        //             filterType: "PRICE_FILTER",
        //             tickSize: "0.01",
        //           },
        //           {
        //             stepSize: "0.00001",
        //             filterType: "LOT_SIZE",
        //             maxQty: "9000",
        //             minQty: "0.00001",
        //           },
        //           {
        //             stepSize: "0.00001",
        //             filterType: "MARKET_LOT_SIZE",
        //             maxQty: "9000",
        //             minQty: "0.00001",
        //           },
        //           {
        //             limit: "200",
        //             filterType: "MAX_NUM_ORDERS",
        //           },
        //           {
        //             minNotional: "5",
        //             filterType: "MIN_NOTIONAL",
        //           },
        //           {
        //             minNotional: "5",
        //             avgPriceMins: "5",
        //             applyMinToMarket: true,
        //             filterType: "NOTIONAL",            // only in SPOT
        //             applyMaxToMarket: true,
        //           },
        //           {
        //             multiplierDown: "0.2",
        //             multiplierUp: "5",
        //             multiplierDecimal: "1",
        //             filterType: "PERCENT_PRICE",
        //           },
        //           {
        //             bidMultiplierUp: "5",
        //             askMultiplierUp: "5",
        //             bidMultiplierDown: "0.2",
        //             avgPriceMins: "5",
        //             multiplierDecimal: "1",
        //             filterType: "PERCENT_PRICE_BY_SIDE",  // only in SPOT
        //             askMultiplierDown: "0.2",
        //           },
        //         ],
        //         orderTypes: [ "LIMIT", "MARKET", "STOP", "STOP_MARKET", "TAKE_PROFIT", "TAKE_PROFIT_MARKET", "TRAILING_STOP_MARKET", ],
        //         timeInForce: [ "GTC", "IOC", "FOK", "GTX", "HIDDEN", ],
        //       }
        //     ]
        //
        //
        const fapiRowsFiltered: List = [];
        for (let i = 0; i < fapiRows.length; i++) {
            const market = fapiRows[i];
            // tmp skip some markets with base = undefined
            if (this.safeString (market, 'baseAsset')) {
                fapiRowsFiltered.push (market);
            }
        }
        const rows = this.arrayConcat (sapiRows, fapiRowsFiltered);
        return this.parseMarkets (rows);
    }

    override parseMarket (market: Dict): Market {
        const id = this.safeString (market, 'symbol');
        const baseId = this.safeString (market, 'baseAsset');
        const quoteId = this.safeString (market, 'quoteAsset');
        const base = this.safeCurrencyCode (baseId);
        const quote = this.safeCurrencyCode (quoteId);
        const active = this.safeString (market, 'status') === 'TRADING';
        let spot: Bool = undefined;
        let symbol: Str = undefined;
        let settle: Str = undefined;
        let settleId: Str = undefined;
        let swap: Bool = undefined;
        let linear: Bool = undefined;
        let inverse: Bool = undefined;
        let contractSize: Num = undefined;
        const contractType = this.safeString (market, 'contractType');
        const isContract = contractType !== undefined;
        if (isContract) {
            // currently, there is only perpetuals, not futures
            spot = false;
            swap = true;
            settleId = this.safeString (market, 'marginAsset');
            settle = this.safeCurrencyCode (settleId);
            symbol = base + '/' + quote + ':' + settle;
            linear = settle === quote;
            inverse = settle === base;
            contractSize = this.safeNumber2 (market, 'contractSize', 'unit', this.parseNumber ('1'));
        } else {
            spot = true;
            swap = false;
            symbol = base + '/' + quote;
        }
        // filters
        const filters = this.safeList (market, 'filters', []);
        const filtersByType = this.indexBy (filters, 'filterType');
        const filterNotional = this.safeDict2 (filtersByType, 'MIN_NOTIONAL', 'NOTIONAL');
        const filterPrice = this.safeDict (filtersByType, 'PRICE_FILTER');
        const filterLotSize = this.safeDict (filtersByType, 'LOT_SIZE');
        const filterMarketLotSize = this.safeDict (filtersByType, 'MARKET_LOT_SIZE', {});
        let pricePrecision = this.safeNumber (filterPrice, 'tickSize');
        if (pricePrecision === undefined) {
            pricePrecision = this.parseNumber (this.parsePrecision (this.safeString (market, 'pricePrecision')));
        }
        const amountPrecision = (filterLotSize !== undefined) ? this.safeNumber (filterLotSize, 'stepSize') : this.parseNumber (this.parsePrecision (this.safeString (market, 'quantityPrecision')));
        return this.safeMarketStructure ({
            'id': id,
            'symbol': symbol,
            'base': base,
            'quote': quote,
            'settle': settle,
            'baseId': baseId,
            'quoteId': quoteId,
            'settleId': settleId,
            'type': isContract ? 'swap' : 'spot',
            'spot': spot,
            'margin': false,
            'swap': swap,
            'future': false,
            'option': false,
            'active': active,
            'contract': isContract,
            'linear': linear,
            'inverse': inverse,
            'taker': this.fees['trading']['taker'],
            'maker': this.fees['trading']['maker'],
            'contractSize': contractSize,
            'expiry': undefined,
            'expiryDatetime': undefined,
            'strike': undefined,
            'optionType': undefined,
            'precision': {
                'amount': amountPrecision,
                'price': pricePrecision,
                'base': this.parseNumber (this.parsePrecision (this.safeString (market, 'baseAssetPrecision'))),
                'quote': this.parseNumber (this.parsePrecision (this.safeString (market, 'quotePrecision'))),
            },
            'limits': {
                'leverage': {
                    'min': undefined,
                    'max': undefined,
                },
                'amount': {
                    'min': this.safeNumber (filterLotSize, 'minQty'),
                    'max': this.safeNumber (filterLotSize, 'maxQty'),
                },
                'price': {
                    'min': this.safeNumber (filterPrice, 'minPrice'),
                    'max': this.safeNumber (filterPrice, 'maxPrice'),
                },
                'cost': {
                    'min': this.safeNumber2 (filterNotional, 'notional', 'minNotional'),
                    'max': undefined,
                },
                'market': {
                    'min': this.safeNumber (filterMarketLotSize, 'minQty'),
                    'max': this.safeNumber (filterMarketLotSize, 'maxQty'),
                },
            },
            'created': this.safeInteger2 (market, 'listingTime', 'createTime'),
            'info': market,
        });
    }

    /**
     * @method
     * @name aster#fetchTime
     * @description fetches the current integer timestamp in milliseconds from the exchange server
     * @see https://asterdex.github.io/aster-api-website/spot-v3/market-data/#get-server-time
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#check-server-time
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {int} the current integer timestamp in milliseconds from the exchange server
     */
    override async fetchTime (params = {}): Promise<Int> {
        let marketType: Str = undefined;
        [ marketType, params ] = this.handleMarketTypeAndParams ('fetchTime', undefined, params);
        let response: Dict;
        if (marketType === 'swap') {
            response = await this.fapiPublicGetV3Time (params);
        } else {
            response = await this.sapiPublicGetV3Time (params);
        }
        //
        // both SPOT & PERP has same format
        //
        // {
        //     "serverTime": 1499827319559
        // }
        //
        return this.safeInteger (response, 'serverTime');
    }

    override parseOHLCV (ohlcv: any, market: Market = undefined): OHLCV {
        //
        // spot:
        //
        //     [
        //         1499040000000, // Open time
        //         "0.01634790", // Open
        //         "0.80000000", // High
        //         "0.01575800", // Low
        //         "0.01577100", // Close
        //         "148976.11427815", // Volume
        //         1499644799999, // Close time
        //         "2434.19055334", // Quote asset volume
        //         308, // Number of trades
        //         "1756.87402397", // Taker buy base asset volume
        //         "28.46694368", // Taker buy quote asset volume
        //         "0"  // ??
        //     ]
        //
        return [
            this.safeInteger (ohlcv, 0),
            this.safeNumber (ohlcv, 1),
            this.safeNumber (ohlcv, 2),
            this.safeNumber (ohlcv, 3),
            this.safeNumber (ohlcv, 4),
            this.safeNumber (ohlcv, 5),
        ];
    }

    /**
     * @method
     * @name aster#fetchOHLCV
     * @description fetches historical candlestick data containing the open, high, low, and close price, and the volume of a market
     * @see https://asterdex.github.io/aster-api-website/spot-v3/market-data/#k-line-data
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#klinecandlestick-data
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#index-price-klinecandlestick-data
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#mark-price-klinecandlestick-data
     * @param {string} symbol unified symbol of the market to fetch OHLCV data for
     * @param {string} timeframe the length of time each candle represents
     * @param {int} [since] timestamp in ms of the earliest candle to fetch
     * @param {int} [limit] the maximum amount of candles to fetch
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.price] "mark" or "index" for mark price and index price candles
     * @param {int} [params.until] the latest time in ms to fetch orders for
     * @returns {int[][]} A list of candles ordered as timestamp, open, high, low, close, volume
     */
    override async fetchOHLCV (symbol: string, timeframe = '1m', since: Int = undefined, limit: Int = undefined, params = {}): Promise<OHLCV[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        let request: Dict = {};
        if (since !== undefined) {
            request['startTime'] = since;
        }
        if (limit !== undefined) {
            request['limit'] = Math.min (limit, 1500);
        }
        [ request, params ] = this.handleUntilOption ('endTime', request, params);
        request['interval'] = this.safeString (this.timeframes, timeframe, timeframe);
        const price = this.safeString (params, 'price');
        const isMark = (price === 'mark');
        const isIndex = (price === 'index');
        params = this.omit (params, 'price');
        let response: Dict | List;
        if (isMark) {
            request['symbol'] = market['id'];
            response = await this.fapiPublicGetV3MarkPriceKlines (this.extend (request, params));
        } else if (isIndex) {
            request['pair'] = market['id'];
            response = await this.fapiPublicGetV3IndexPriceKlines (this.extend (request, params));
        } else {
            request['symbol'] = market['id'];
            if (market['linear']) {
                response = await this.fapiPublicGetV3Klines (this.extend (request, params));
            } else {
                response = await this.sapiPublicGetV3Klines (this.extend (request, params));
            }
            //
            // both SPOT & PERP has same format
            //
            //  [
            //     [
            //         1499040000000, // Open time
            //         "0.01634790", // Open
            //         "0.80000000", // High
            //         "0.01575800", // Low
            //         "0.01577100", // Close
            //         "148976.11427815", // Volume
            //         1499644799999, // Close time
            //         "2434.19055334", // Quote asset volume
            //         308, // Number of trades
            //         "1756.87402397", // Taker buy base asset volume
            //         "28.46694368", // Taker buy quote asset volume,
            //         "0"
            //     ]
            //  ]
            //
        }
        return this.parseOHLCVs (this.toArray (response), market, timeframe, since, limit);
    }

    override parseTrade (trade: Dict, market: Market = undefined): Trade {
        //
        // fetchTrades
        //
        //     recent trades:
        //
        //     {
        //         "id": 3913206,
        //         "price": "644.100",
        //         "qty": "0.08",
        //         "quoteQty": "51.528",      // present in PERP
        //         "baseQty": "4.95049505",   // present in SPOT
        //         "time": 1749784506633,
        //         "isBuyerMaker": true
        //     }
        //
        //     aggrTrades
        //
        //     {
        //         "a": 26129, // Aggregate tradeId
        //         "p": "0.01633102", // Price
        //         "q": "4.70443515", // Quantity
        //         "f": 27781, // First tradeId
        //         "l": 27781, // Last tradeId
        //         "T": 1498793709153, // Timestamp
        //         "m": true, // Was the buyer the maker?
        //     }
        //
        // fetchMyTrades  (SPOT & PERP have similar format)
        //
        // {
        //     "symbol": "ETHUSDT",
        //     "id": 2583152,
        //     "orderId": 418588675,
        //     "side": "SELL",
        //     "price": "2330.04",
        //     "qty": "0.0030",
        //     "quoteQty": "6.99000000",
        //     "commission": "0.00279605",
        //     "commissionAsset": "USDT",
        //     "time": 1776409179230,
        //     "counterpartyId": 5143150,   // only in SPOT
        //     "createUpdateId": null,      // only in SPOT
        //     "maker": false,              // only in SPOT
        //     "buyer": false,              // only in SPOT
        //     "realizedPnl": "0.00029999", // only in PERP
        //     "marginAsset": "USDT",       // only in PERP
        //     "positionSide": "BOTH",      // only in PERP
        // }
        //
        const id = this.safeString2 (trade, 'id', 'a');
        const marketId = this.safeString (trade, 'symbol');
        const marketType = ('positionSide' in trade) ? 'swap' : 'spot';
        market = this.safeMarket (marketId, market, undefined, marketType);
        const currencyId = this.safeString2 (trade, 'commissionAsset', 'marginAsset');
        const currencyCode = this.safeCurrencyCode (currencyId);
        const amountString = this.safeString2 (trade, 'qty', 'q');
        const priceString = this.safeString2 (trade, 'price', 'p');
        const costString = this.safeString2 (trade, 'quoteQty', 'baseQty');
        const timestamp = this.safeInteger2 (trade, 'time', 'T');
        let side = this.safeStringLower (trade, 'side');
        const isMaker = this.safeBool (trade, 'maker');
        let takerOrMaker: Str = undefined;
        if (isMaker !== undefined) {
            takerOrMaker = isMaker ? 'maker' : 'taker';
            if (side === undefined) {
                const isBuyer = this.safeBool (trade, 'buyer');
                if (isBuyer !== undefined) {
                    side = isBuyer ? 'buy' : 'sell';
                }
            }
        }
        const isBuyerMaker = this.safeBool2 (trade, 'isBuyerMaker', 'm');
        if (isBuyerMaker !== undefined) {
            side = isBuyerMaker ? 'sell' : 'buy';
        }
        return this.safeTrade ({
            'id': id,
            'info': trade,
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'symbol': market['symbol'],
            'order': this.safeString (trade, 'orderId'),
            'type': undefined,
            'side': side,
            'takerOrMaker': takerOrMaker,
            'price': priceString,
            'amount': amountString,
            'cost': costString,
            'fee': {
                'cost': this.parseNumber (Precise.stringAbs (this.safeString (trade, 'commission'))),
                'currency': currencyCode,
            },
        }, market);
    }

    /**
     * @method
     * @name aster#fetchTrades
     * @description get the list of most recent trades for a particular symbol
     * @see https://asterdex.github.io/aster-api-website/spot-v3/market-data/#recent-trades-list
     * @see https://asterdex.github.io/aster-api-website/spot-v3/market-data/#recent-trades-aggregated
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#recent-trades-list
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#compressedaggregate-trades-list
     * @param {string} symbol unified symbol of the market to fetch trades for
     * @param {int} [since] timestamp in ms of the earliest trade to fetch
     * @param {int} [limit] the maximum amount of trades to fetch
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {Trade[]} a list of [trade structures]{@link https://docs.ccxt.com/?id=public-trades}
     */
    override async fetchTrades (symbol: string, since: Int = undefined, limit: Int = undefined, params = {}): Promise<Trade[]> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        let request: Dict = {
            'symbol': market['id'],
        };
        if (limit !== undefined) {
            request['limit'] = Math.min (limit, 1000);
        }
        let response: List;
        const sinceDefined = since !== undefined;
        const untilDefined = ('until' in params);
        if (sinceDefined) {
            request['startTime'] = since;
        }
        if (untilDefined) {
            request = this.handleUntilOption ('endTime', request, params);
        }
        // use historical endpoint for targeted requests
        if ('startTime' in request) {
            if (market['swap']) {
                response = await this.fapiPublicGetV3AggTrades (this.extend (request, params));
            } else {
                response = await this.sapiPublicGetV3AggTrades (this.extend (request, params));
            }
            //
            // both FAPI and SAPI have same response format
            //
            // [
            //     {
            //         "a": 26129, // Aggregate tradeId
            //         "p": "0.01633102", // Price
            //         "q": "4.70443515", // Quantity
            //         "f": 27781, // First tradeId
            //         "l": 27781, // Last tradeId
            //         "T": 1498793709153, // Timestamp
            //         "m": true, // Was the buyer the maker?
            //     }
            // ]
            //
        } else {
            if (market['swap']) {
                response = await this.fapiPublicGetV3Trades (this.extend (request, params));
            } else {
                response = await this.sapiPublicGetV3Trades (this.extend (request, params));
            }
            //
            // SAPI & FAPI have only one field difference
            //
            //    [
            //        {
            //            "id": "73620768",
            //            "price": "2324.07",
            //            "qty": "0.430",
            //            "quoteQty": "999.35",      // only in PERP
            //             "baseQty": "4.95049505",  // only in SPOT
            //            "time": "1776407252900",
            //            "isBuyerMaker": false
            //        }, ...
            //
        }
        return this.parseTrades (response, market, since, limit);
    }

    /**
     * @method
     * @name aster#fetchMyTrades
     * @description fetch all trades made by the user
     * @see https://asterdex.github.io/aster-api-website/spot-v3/account%26trades/#account-trade-history-user_data
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#account-trade-list-user_data
     * @param {string} [symbol] unified market symbol
     * @param {int} [since] the earliest time in ms to fetch trades for
     * @param {int} [limit] the maximum number of trades structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.until] timestamp in ms for the ending date filter, default is undefined
     * @returns {object[]} a list of [trade structures]{@link https://docs.ccxt.com/?id=trade-structure}
     */
    override async fetchMyTrades (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}) {
        await this.loadMarketsAndSignIn ();
        let request: Dict = {};
        let market: Market = undefined;
        if (symbol !== undefined) {
            market = this.market (symbol);
            request['symbol'] = market['id'];
        }
        let marketType: Str = undefined;
        [ marketType, params ] = this.handleMarketTypeAndParams ('fetchMyTrades', market, params);
        if (since !== undefined) {
            request['startTime'] = since;
        }
        if (limit !== undefined) {
            request['limit'] = Math.min (limit, 1000);
        }
        [ request, params ] = this.handleUntilOption ('endTime', request, params);
        let response: List;
        if (marketType === 'swap') {
            response = await this.fapiPrivateGetV3UserTrades (this.extend (request, params));
        } else {
            response = await this.sapiPrivateGetV3UserTrades (this.extend (request, params));
        }
        //
        // SPOT & PERP have similar format
        //
        // {
        //     "symbol": "ETHUSDT",
        //     "id": 2583152,
        //     "orderId": 418588675,
        //     "side": "SELL",
        //     "price": "2330.04",
        //     "qty": "0.0030",
        //     "quoteQty": "6.99000000",
        //     "commission": "0.00279605",
        //     "commissionAsset": "USDT",
        //     "time": 1776409179230,
        //     "counterpartyId": 5143150,   // only in PERP
        //     "createUpdateId": null,      // only in PERP
        //     "maker": false,              // only in PERP
        //     "buyer": false,              // only in PERP
        //     "realizedPnl": "0.00029999", // only in SPOT
        //     "marginAsset": "USDT",       // only in SPOT
        //     "positionSide": "BOTH",      // only in SPOT
        // }
        //
        return this.parseTrades (response, market, since, limit, params);
    }

    /**
     * @method
     * @name aster#fetchOrderBook
     * @description fetches information on open orders with bid (buy) and ask (sell) prices, volumes and other data
     * @see https://asterdex.github.io/aster-api-website/spot-v3/market-data/#depth-information
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#order-book
     * @param {string} symbol unified symbol of the market to fetch the order book for
     * @param {int} [limit] the maximum amount of order book entries to return
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} an [order book structure]{@link https://docs.ccxt.com/?id=order-book-structure}
     */
    override async fetchOrderBook (symbol: string, limit: Int = undefined, params = {}): Promise<OrderBook> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        const request: Dict = {
            'symbol': market['id'],
        };
        let response: Dict;
        if (limit !== undefined) {
            request['limit'] = this.findNearestCeiling ([ 5, 10, 20, 50, 100, 500, 1000 ], limit);
        }
        if (market['swap']) {
            response = await this.fapiPublicGetV3Depth (this.extend (request, params));
        } else {
            response = await this.sapiPublicGetV3Depth (this.extend (request, params));
        }
        //
        // both SPOT & PERP has same format
        //
        //     {
        //         "lastUpdateId": 1027024,
        //         "E": 1589436922972, //     Message output time
        //         "T": 1589436922959, //     Transaction time
        //         "bids": [
        //             [
        //                 "4.00000000", //     PRICE
        //                 "431.00000000" //     QTY
        //             ]
        //         ],
        //         "asks": [
        //             [
        //                 "4.00000200",
        //                 "12.00000000"
        //             ]
        //         ]
        //     }
        //
        const timestamp = this.safeInteger (response, 'T');
        return this.parseOrderBook (response, symbol, timestamp, 'bids', 'asks');
    }

    override parseTicker (ticker: Dict, market: Market = undefined): Ticker {
        //
        // fetchTicker & fetchTickers: both SPOT & PERP has similar format
        //
        //    {
        //        "symbol": "ETHUSDT",
        //        "priceChange": "6.54",
        //        "priceChangePercent": "0.279",
        //        "weightedAvgPrice": "2330.70",
        //        "lastPrice": "2350.00",
        //        "lastQty": "4.437",
        //        "openPrice": "2343.46",
        //        "highPrice": "2363.20",
        //        "lowPrice": "2283.86",
        //        "volume": "267154.248",
        //        "quoteVolume": "622657018.70",
        //        "openTime": "1776329400000",
        //        "closeTime": "1776415832593",
        //        "firstId": "73520536",
        //        "lastId": "73630176",
        //        "count": "109640",
        //        "baseAsset": "BTC",            // only in SPOT
        //        "quoteAsset": "USDT",          // only in SPOT
        //        "bidPrice": "71125.98",        // only in SPOT
        //        "bidQty": "0.00737",           // only in SPOT
        //        "askPrice": "71152.10",        // only in SPOT
        //        "askQty": "0.32399"            // only in SPOT
        //    }
        //
        //
        // fetchBidsAsks: SPOT & PERP have only one field difference
        //
        //     [
        //        {
        //            "symbol": "BMTUSDT",
        //            "bidPrice": "0.004000",
        //            "bidQty": "1250.0",
        //            "askPrice": "0.000000",
        //            "askQty": "0.0",
        //            "time": "1776411276072",
        //            "lastUpdateId": "453174307613"   // only in PERP
        //        }, ...
        //
        const timestamp = this.safeInteger (ticker, 'closeTime');
        const last = this.safeString (ticker, 'lastPrice');
        const open = this.safeString (ticker, 'openPrice');
        const percentage = this.safeString (ticker, 'priceChangePercent');
        const quoteVolume = this.safeString (ticker, 'quoteVolume');
        const baseVolume = this.safeString (ticker, 'volume');
        const high = this.safeString (ticker, 'highPrice');
        const low = this.safeString (ticker, 'lowPrice');
        const isTickerResponse = ('priceChange' in ticker);
        let marketType: Str = undefined;
        if (isTickerResponse) {
            marketType = ('baseAsset' in ticker) ? 'spot' : 'swap';
        } else {
            marketType = ('lastUpdateId' in ticker) ? 'swap' : 'spot';
        }
        const marketId = this.safeString (ticker, 'symbol');
        market = this.safeMarket (marketId, market, undefined, marketType);
        return this.safeTicker ({
            'symbol': market['symbol'],
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'high': high,
            'low': low,
            'bid': this.safeString (ticker, 'bidPrice'),
            'bidVolume': this.safeString (ticker, 'bidQty'),
            'ask': this.safeString (ticker, 'askPrice'),
            'askVolume': this.safeString (ticker, 'askQty'),
            'vwap': undefined,
            'open': open,
            'close': last,
            'last': last,
            'previousClose': undefined,
            'change': undefined,
            'percentage': percentage,
            'average': undefined,
            'baseVolume': baseVolume,
            'quoteVolume': quoteVolume,
            'markPrice': undefined,
            'indexPrice': undefined,
            'info': ticker,
        }, market);
    }

    /**
     * @method
     * @name aster#fetchTicker
     * @description fetches a price ticker, a statistical calculation with the information calculated over the past 24 hours for a specific market
     * @see https://asterdex.github.io/aster-api-website/spot-v3/market-data/#24h-price-change
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#24hr-ticker-price-change-statistics
     * @param {string} symbol unified symbol of the market to fetch the ticker for
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [ticker structure]{@link https://docs.ccxt.com/?id=ticker-structure}
     */
    override async fetchTicker (symbol: string, params = {}): Promise<Ticker> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        const request: Dict = {
            'symbol': market['id'],
        };
        let response: Dict;
        if (market['swap']) {
            response = await this.fapiPublicGetV3Ticker24hr (this.extend (request, params));
        } else {
            response = await this.sapiPublicGetV3Ticker24hr (this.extend (request, params));
        }
        //
        // both SPOT & PERP has same format
        //
        //    {
        //        "symbol": "ETHUSDT",
        //        "priceChange": "6.54",
        //        "priceChangePercent": "0.279",
        //        "weightedAvgPrice": "2330.70",
        //        "lastPrice": "2350.00",
        //        "lastQty": "4.437",
        //        "openPrice": "2343.46",
        //        "highPrice": "2363.20",
        //        "lowPrice": "2283.86",
        //        "volume": "267154.248",
        //        "quoteVolume": "622657018.70",
        //        "openTime": "1776329400000",
        //        "closeTime": "1776415832593",
        //        "firstId": "73520536",
        //        "lastId": "73630176",
        //        "count": "109640",
        //        "baseAsset": "BTC",            // only in SPOT
        //        "quoteAsset": "USDT",          // only in SPOT
        //        "bidPrice": "71125.98",        // only in SPOT
        //        "bidQty": "0.00737",           // only in SPOT
        //        "askPrice": "71152.10",        // only in SPOT
        //        "askQty": "0.32399"            // only in SPOT
        //    }
        //
        return this.parseTicker (response, market);
    }

    /**
     * @method
     * @name aster#fetchTickers
     * @description fetches price tickers for multiple markets, statistical information calculated over the past 24 hours for each market
     * @see https://asterdex.github.io/aster-api-website/spot-v3/market-data/#24h-price-change
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#24hr-ticker-price-change-statistics
     * @param {string[]} symbols unified symbols of the markets to fetch the ticker for, all market tickers are returned if not assigned
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.subType] "linear" or "inverse"
     * @param {string} [params.type] 'spot', 'option', use params["subType"] for swap and future markets
     * @returns {object} an array of [ticker structures]{@link https://docs.ccxt.com/?id=ticker-structure}
     */
    override async fetchTickers (symbols: Strings = undefined, params = {}): Promise<Tickers> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        symbols = this.marketSymbols (symbols, undefined, true, true, true);
        const market = this.getMarketFromSymbols (symbols);
        let marketType: Str = undefined;
        [ marketType, params ] = this.handleMarketTypeAndParams ('fetchTickers', market, params);
        let response: NullableDict = undefined;
        if (marketType === 'swap') {
            response = await this.fapiPublicGetV3Ticker24hr (params);
        } else if (marketType === 'spot') {
            response = await this.sapiPublicGetV3Ticker24hr (params);
        }
        //
        //     [
        //         {
        //             "symbol": "BTCUSDT",
        //             "priceChange": "1845.7",
        //             "priceChangePercent": "1.755",
        //             "weightedAvgPrice": "105515.5",
        //             "lastPrice": "107037.7",
        //             "lastQty": "0.004",
        //             "openPrice": "105192.0",
        //             "highPrice": "107223.5",
        //             "lowPrice": "104431.6",
        //             "volume": "8753.286",
        //             "quoteVolume": "923607368.61",
        //             "openTime": 1749976620000,
        //             "closeTime": 1750063053754,
        //             "firstId": 24195078,
        //             "lastId": 24375783,
        //             "count": 180706,
        //             "baseAsset": "BTC",              // only in SPOT
        //             "quoteAsset": "USDT",            // only in SPOT
        //             "bidPrice": "71125.98",          // only in SPOT
        //             "bidQty": "0.00737",             // only in SPOT
        //             "askPrice": "71152.10",          // only in SPOT
        //             "askQty": "0.32399"              // only in SPOT
        //         }
        //     ]
        //
        return this.parseTickers (response, symbols);
    }

    /**
     * @method
     * @name aster#fetchLastPrices
     * @description fetches the last price for multiple markets
     * @see https://asterdex.github.io/aster-api-website/spot-v3/market-data/#latest-price
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#symbol-price-ticker
     * @param {string[]|undefined} symbols unified symbols of the markets to fetch the last prices
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.subType] "linear" or "inverse"
     * @returns {object} a dictionary of lastprices structures
     */
    override async fetchLastPrices (symbols: Strings = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        symbols = this.marketSymbols (symbols, undefined, true, true, true);
        const market = this.getMarketFromSymbols (symbols);
        let marketType: Str = undefined;
        [ marketType, params ] = this.handleMarketTypeAndParams ('fetchLastPrices', market, params);
        let response: Dict | List | undefined = undefined;
        if (marketType === 'swap') {
            response = await this.fapiPublicGetV3TickerPrice (params);
        } else if (marketType === 'spot') {
            response = await this.sapiPublicGetV3TickerPrice (params);
        }
        //
        // both SPOT & SWAP has same format
        //
        //     [
        //         {
        //             "symbol": "LTCBTC",
        //             "price": "4.00000200"
        //             "time": "1649666690902"
        //         },
        //         ...
        //     ]
        //
        if (response === undefined) {
            throw new NullResponse (this.id + ' fetchLastPrices() returned empty response');
        }
        const rows = this.toArray (response);
        const results: List = [];
        for (let i = 0; i < rows.length; i++) {
            const marketId = this.safeString (rows[i], 'symbol');
            const safeMarket = this.safeMarket (marketId, undefined, undefined, marketType);
            const priceData = this.extend (this.parseLastPrice (rows[i], safeMarket), params);
            results.push (priceData);
        }
        symbols = this.marketSymbols (symbols);
        return this.filterByArray (results, 'symbol', symbols) as LastPrices;
    }

    override parseLastPrice (entry: any, market: Market = undefined) {
        //
        // spot & swap
        //
        //     {
        //         "symbol": "LTCBTC",
        //         "price": "4.00000200"
        //         "time": "1649666690902"
        //     }
        //
        const timestamp = this.safeInteger (entry, 'time');
        return {
            'symbol': this.safeString (market, 'symbol'),
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'price': this.safeNumberOmitZero (entry, 'price'),
            'side': undefined,
            'info': entry,
        };
    }

    /**
     * @method
     * @name aster#fetchBidsAsks
     * @description fetches the bid and ask price and volume for multiple markets
     * @see https://asterdex.github.io/aster-api-website/spot-v3/market-data/#current-best-order
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#symbol-order-book-ticker
     * @param {string[]|undefined} symbols unified symbols of the markets to fetch the bids and asks for, all markets are returned if not assigned
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.subType] "linear" or "inverse"
     * @returns {object} a dictionary of [ticker structures]{@link https://docs.ccxt.com/?id=ticker-structure}
     */
    override async fetchBidsAsks (symbols: Strings = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        symbols = this.marketSymbols (symbols, undefined, true, true, true);
        const market = this.getMarketFromSymbols (symbols);
        let marketType: Str = undefined;
        [ marketType, params ] = this.handleMarketTypeAndParams ('fetchBidsAsks', market, params);
        let response: NullableDict = undefined;
        if (marketType === 'swap') {
            response = await this.fapiPublicGetV3TickerBookTicker (params);
        } else if (marketType === 'spot') {
            response = await this.sapiPublicGetV3TickerBookTicker (params);
        }
        //
        // SPOT & PERP have only one field difference
        //
        //     [
        //        {
        //            "symbol": "BMTUSDT",
        //            "bidPrice": "0.004000",
        //            "bidQty": "1250.0",
        //            "askPrice": "0.000000",
        //            "askQty": "0.0",
        //            "time": "1776411276072",
        //            "lastUpdateId": "453174307613"   // only in PERP
        //        }, ...
        //
        return this.parseTickers (response, symbols);
    }

    override parseFundingRate (contract: any, market: Market = undefined): FundingRate {
        //
        // fundingRate
        //
        //     {
        //         "symbol": "BTCUSDT",
        //         "markPrice": "106729.84047826",
        //         "indexPrice": "106775.72673913",
        //         "estimatedSettlePrice": "106708.84997006",
        //         "lastFundingRate": "0.00010000",
        //         "interestRate": "0.00010000",
        //         "nextFundingTime": 1750147200000,
        //         "time": 1750146970000
        //     }
        //
        // funding interval
        //
        //     {
        //         "symbol": "INJUSDT",
        //         "interestRate": "0.00010000",
        //         "time": 1756197479000,
        //         "fundingIntervalHours": 8,
        //         "fundingFeeCap": 0.03,
        //         "fundingFeeFloor": -0.03
        //     }
        //
        const marketId = this.safeString (contract, 'symbol');
        const nextFundingTimestamp = this.safeInteger (contract, 'nextFundingTime');
        const timestamp = this.safeInteger (contract, 'time');
        const interval = this.safeString (contract, 'fundingIntervalHours');
        let intervalString: Str = undefined;
        if (interval !== undefined) {
            intervalString = interval + 'h';
        }
        return {
            'info': contract,
            'symbol': this.safeSymbol (marketId, market, undefined, 'contract'),
            'markPrice': this.safeNumber (contract, 'markPrice'),
            'indexPrice': this.safeNumber (contract, 'indexPrice'),
            'interestRate': this.safeNumber (contract, 'interestRate'),
            'estimatedSettlePrice': this.safeNumber (contract, 'estimatedSettlePrice'),
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'fundingRate': this.safeNumber (contract, 'lastFundingRate'),
            'fundingTimestamp': undefined,
            'fundingDatetime': undefined,
            'nextFundingRate': undefined,
            'nextFundingTimestamp': nextFundingTimestamp,
            'nextFundingDatetime': this.iso8601 (nextFundingTimestamp),
            'previousFundingRate': undefined,
            'previousFundingTimestamp': undefined,
            'previousFundingDatetime': undefined,
            'interval': intervalString,
        } as FundingRate;
    }

    /**
     * @method
     * @name aster#fetchFundingRate
     * @description fetch the current funding rate
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#symbol-price-ticker
     * @param {string} symbol unified market symbol
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [funding rate structure]{@link https://docs.ccxt.com/?id=funding-rate-structure}
     */
    override async fetchFundingRate (symbol: string, params = {}): Promise<FundingRate> {
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' fetchFundingRate() requires a symbol argument');
        }
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        const market = this.market (symbol);
        const request: Dict = {
            'symbol': market['id'],
        };
        const response = await this.fapiPublicGetV3PremiumIndex (this.extend (request, params));
        //
        //     {
        //         "symbol": "BTCUSDT",
        //         "markPrice": "106729.84047826",
        //         "indexPrice": "106775.72673913",
        //         "estimatedSettlePrice": "106708.84997006",
        //         "lastFundingRate": "0.00010000",
        //         "interestRate": "0.00010000",
        //         "nextFundingTime": 1750147200000,
        //         "time": 1750146970000
        //     }
        //
        return this.parseFundingRate (response, market);
    }

    /**
     * @method
     * @name aster#fetchFundingRates
     * @description fetch the current funding rate for multiple symbols
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#symbol-price-ticker
     * @param {string[]} [symbols] list of unified market symbols
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [funding rate structures]{@link https://docs.ccxt.com/?id=funding-rate-structure}
     */
    override async fetchFundingRates (symbols: Strings = undefined, params = {}): Promise<FundingRates> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        symbols = this.marketSymbols (symbols);
        const response = await this.fapiPublicGetV3PremiumIndex (this.extend (params));
        //
        //     [
        //         {
        //             "symbol": "BTCUSDT",
        //             "markPrice": "106729.84047826",
        //             "indexPrice": "106775.72673913",
        //             "estimatedSettlePrice": "106708.84997006",
        //             "lastFundingRate": "0.00010000",
        //             "interestRate": "0.00010000",
        //             "nextFundingTime": 1750147200000,
        //             "time": 1750146970000
        //         }
        //     ]
        //
        return this.parseFundingRates (response, symbols);
    }

    /**
     * @method
     * @name aster#fetchFundingIntervals
     * @description fetch the funding rate interval for multiple markets
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#get-funding-rate-config
     * @param {string[]} [symbols] list of unified market symbols
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [funding rate structures]{@link https://docs.ccxt.com/?id=funding-rate-structure}
     */
    override async fetchFundingIntervals (symbols: Strings = undefined, params = {}): Promise<FundingRates> {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        if (symbols !== undefined) {
            symbols = this.marketSymbols (symbols);
        }
        const response = await this.fapiPublicGetV3FundingInfo (params);
        //
        //     [
        //         {
        //             "symbol": "INJUSDT",
        //             "interestRate": "0.00010000",
        //             "time": 1756197479000,
        //             "fundingIntervalHours": 8,
        //             "fundingFeeCap": 0.03,
        //             "fundingFeeFloor": -0.03
        //         }
        //     ]
        //
        return this.parseFundingRates (response, symbols);
    }

    /**
     * @method
     * @name aster#fetchFundingRateHistory
     * @description fetches historical funding rate prices
     * @see https://asterdex.github.io/aster-api-website/futures-v3/market-data/#get-funding-rate-history
     * @param {string} symbol unified symbol of the market to fetch the funding rate history for
     * @param {int} [since] timestamp in ms of the earliest funding rate to fetch
     * @param {int} [limit] the maximum amount of [funding rate structures]{@link https://docs.ccxt.com/?id=funding-rate-history-structure} to fetch
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.until] timestamp in ms of the latest funding rate
     * @returns {object[]} a list of [funding rate structures]{@link https://docs.ccxt.com/?id=funding-rate-history-structure}
     */
    override async fetchFundingRateHistory (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}) {
        if (this.markets === undefined) {
            await this.loadMarkets ();
        }
        let request: Dict = {};
        let market: Market = undefined;
        if (symbol !== undefined) {
            market = this.market (symbol);
            request['symbol'] = market['id'];
        }
        if (since !== undefined) {
            request['startTime'] = since;
        }
        if (limit !== undefined) {
            request['limit'] = Math.min (limit, 1000);
        }
        [ request, params ] = this.handleUntilOption ('endTime', request, params);
        const response = await this.fapiPublicGetV3FundingRate (this.extend (request, params));
        //
        //     [
        //         {
        //             "symbol": "BTCUSDT",
        //             "fundingTime": 1747209600000,
        //             "fundingRate": "0.00010000"
        //         }
        //     ]
        //
        return this.parseFundingRateHistories (response, market);
    }

    override parseFundingRateHistory (contract: any, market: Market = undefined) {
        //
        //     {
        //         "symbol": "BTCUSDT",
        //         "fundingRate": "0.00063521",
        //         "fundingTime": "1621267200000",
        //     }
        //
        const timestamp = this.safeInteger (contract, 'fundingTime');
        return {
            'info': contract,
            'symbol': this.safeSymbol (this.safeString (contract, 'symbol'), undefined, undefined, 'swap'),
            'fundingRate': this.safeNumber (contract, 'fundingRate'),
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
        };
    }

    /**
     * @method
     * @name aster#fetchBalance
     * @description query for balance and get the amount of funds available for trading or funds locked in orders
     * @see https://asterdex.github.io/aster-api-website/spot-v3/account%26trades/#account-information-user_data
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#futures-account-balance-v3-user_data
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.subType] "linear" or "inverse"
     * @param {string} [params.type] 'spot', 'option', use params["subType"] for swap and future markets
     * @returns {object} a [balance structure]{@link https://docs.ccxt.com/?id=balance-structure}
     */
    override async fetchBalance (params = {}): Promise<Balances> {
        await this.loadMarketsAndSignIn ();
        let marketType: Str = undefined;
        [ marketType, params ] = this.handleMarketTypeAndParams ('fetchBalance', undefined, params);
        let response: NullableDict = undefined;
        let data: Dict | List | undefined = undefined;
        if (marketType === 'swap') {
            data = await this.fapiPrivateGetV3Balance (params);
            //
            //    [
            //        {
            //            "accountAlias": "FzXquXsRFzXqAufW",
            //            "asset": "CDL",
            //            "balance": "0.00000000",
            //            "crossWalletBalance": "0.00000000",
            //            "crossUnPnl": "0.00000000",
            //            "availableBalance": "878.90500233",
            //            "maxWithdrawAmount": "0.00000000",
            //            "marginAvailable": true,
            //            "updateTime": "0"
            //        }, ...
            //
        } else if (marketType === 'spot') {
            response = await this.sapiPrivateGetV3Account (params);
            data = this.safeList (response, 'balances', []);
            //
            //     [
            //         {
            //             "asset": "BTC",
            //             "free": "4723846.89208129",
            //             "locked": "0.00000000"
            //         }
            //     ]
            //
        }
        return this.parseBalance (data);
    }

    override parseBalance (response: any): Balances {
        const result: Dict = { 'info': response };
        for (let i = 0; i < response.length; i++) {
            const balance = response[i];
            const currencyId = this.safeString (balance, 'asset');
            const code = this.safeCurrencyCode (currencyId);
            const account = this.account ();
            account['free'] = this.safeString2 (balance, 'free', 'availableBalance');
            account['used'] = this.safeString (balance, 'locked');
            account['total'] = this.safeString (balance, 'balance');
            if (code !== undefined) {
                result[code] = account;
            }
        }
        return this.safeBalance (result);
    }

    /**
     * @method
     * @name aster#setMarginMode
     * @description set margin mode to 'cross' or 'isolated'
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#change-margin-type-trade
     * @param {string} marginMode 'cross' or 'isolated'
     * @param {string} symbol unified market symbol
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} response from the exchange
     */
    override async setMarginMode (marginMode: string, symbol: Str = undefined, params = {}) {
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' setMarginMode() requires a symbol argument');
        }
        marginMode = marginMode.toUpperCase ();
        if (marginMode === 'CROSS') {
            marginMode = 'CROSSED';
        }
        if ((marginMode !== 'ISOLATED') && (marginMode !== 'CROSSED')) {
            throw new BadRequest (this.id + ' marginMode must be either isolated or cross');
        }
        await this.loadMarketsAndSignIn ();
        const market = this.market (symbol);
        const request: Dict = {
            'symbol': market['id'],
            'marginType': marginMode,
        };
        const response = await this.fapiPrivatePostV3MarginType (this.extend (request, params));
        //
        //     { "code": 200,"msg": "success" }
        //
        return response;
    }

    /**
     * @method
     * @name aster#fetchPositionMode
     * @description fetchs the position mode, hedged or one way, hedged for aster is set identically for all linear markets or all inverse markets
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#get-current-position-modeuser_data
     * @param {string} symbol unified symbol of the market to fetch the order book for
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} an object detailing whether the market is in hedged or one-way mode
     */
    override async fetchPositionMode (symbol: Str = undefined, params = {}): Promise<PositionModeInfo> {
        const response = await this.fapiPrivateGetV3PositionSideDual (params);
        //
        //     {
        //         "dualSidePosition": true // "true": Hedge Mode; "false": One-way Mode
        //     }
        //
        return {
            'info': response,
            'hedged': this.safeBool (response, 'dualSidePosition'),
        };
    }

    /**
     * @method
     * @name aster#setPositionMode
     * @description set hedged to true or false for a market
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#change-position-modetrade
     * @param {bool} hedged set to true to use dualSidePosition
     * @param {string} symbol not used by setPositionMode ()
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} response from the exchange
     */
    override async setPositionMode (hedged: boolean, symbol: Str = undefined, params = {}) {
        const strValue = hedged ? 'true' : 'false';
        const request: Dict = {
            'dualSidePosition': strValue,
        };
        //
        //     {
        //         "code": 200,
        //         "msg": "success"
        //     }
        //
        return await this.fapiPrivatePostV3PositionSideDual (this.extend (request, params));
    }

    parseTradingFee (fee: Dict, market: Market = undefined): TradingFeeInterface {
        const marketId = this.safeString (fee, 'symbol');
        market = this.safeMarket (marketId, market);
        const symbol = this.safeSymbol (marketId, market);
        return {
            'info': fee,
            'symbol': symbol,
            'maker': this.safeNumber (fee, 'makerCommissionRate'),
            'taker': this.safeNumber (fee, 'takerCommissionRate'),
            'percentage': false,
            'tierBased': false,
        };
    }

    /**
     * @method
     * @name aster#fetchTradingFee
     * @description fetch the trading fees for a market
     * @see https://asterdex.github.io/aster-api-website/spot-v3/market-data/#get-symbol-fees
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#user-commission-rate-user_data
     * @param {string} symbol unified market symbol
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [fee structure]{@link https://docs.ccxt.com/?id=fee-structure}
     */
    override async fetchTradingFee (symbol: string, params = {}): Promise<TradingFeeInterface> {
        await this.loadMarketsAndSignIn ();
        const market = this.market (symbol);
        const request: Dict = {
            'symbol': market['id'],
        };
        let response: Dict;
        if (market['swap']) {
            response = await this.fapiPrivateGetV3CommissionRate (this.extend (request, params));
        } else {
            response = await this.sapiPrivateGetV3CommissionRate (this.extend (request, params));
        }
        //
        // both SPOT & SWAP has same format
        //
        //     {
        //         "symbol": "BTCUSDT",
        //         "makerCommissionRate": "0.0002",
        //         "takerCommissionRate": "0.0004"
        //     }
        //
        return this.parseTradingFee (response, market);
    }

    parseOrderStatus (status: Str) {
        const statuses: Dict = {
            'NEW': 'open',
            'PARTIALLY_FILLED': 'open',
            'FILLED': 'closed',
            'CANCELED': 'canceled',
            'REJECTED': 'canceled',
            'EXPIRED': 'canceled',
        };
        return this.safeString (statuses, (status as string), status);
    }

    parseOrderType (type: Str) {
        const types: Dict = {
            'LIMIT': 'limit',
            'MARKET': 'market',
            'STOP': 'limit',
            'STOP_MARKET': 'market',
            'TAKE_PROFIT': 'limit',
            'TAKE_PROFIT_MARKET': 'market',
            'TRAILING_STOP_MARKET': 'market',
        };
        return this.safeString (types, (type as string), type);
    }

    override parseOrder (order: Dict, market: Market = undefined): Order {
        //
        // swap
        //     {
        //         "avgPrice": "0.00000",
        //         "clientOrderId": "abc",
        //         "cumQuote": "0",
        //         "executedQty": "0",
        //         "orderId": 1917641,
        //         "origQty": "0.40",
        //         "origType": "TRAILING_STOP_MARKET",
        //         "price": "0",
        //         "reduceOnly": false,
        //         "side": "BUY",
        //         "positionSide": "SHORT",
        //         "status": "NEW",
        //         "stopPrice": "9300",
        //         "closePosition": false,
        //         "symbol": "BTCUSDT",
        //         "time": 1579276756075,
        //         "timeInForce": "GTC",
        //         "type": "TRAILING_STOP_MARKET",
        //         "activatePrice": "9020",
        //         "priceRate": "0.3",
        //         "updateTime": 1579276756075,
        //         "workingType": "CONTRACT_PRICE",
        //         "priceProtect": false
        //     }
        //
        // spot
        //
        //   fetchOrders, fetchOpenOrders, fetchOpenOrder, fetchOrder, cancelOrder, createOrder
        //
        //        {
        //            "orderId": "417594542",
        //            "symbol": "ETHUSDT",
        //            "status": "FILLED",
        //            "clientOrderId": "web_qnvMAhOJsiVbSyu0BdKG",
        //            "price": "0",                     // value set for unfilled
        //            "avgPrice": "2351.580000",        // value zero for unfilled
        //            "origQty": "0.0054",
        //            "executedQty": "0.0054",          // value zero for unfilled
        //            "cumQuote": "12.69853200",        // value zero for unfilled
        //            "timeInForce": "GTC",
        //            "type": "MARKET",
        //            "side": "SELL",
        //            "stopPrice": "0",
        //            "origType": "MARKET",
        //            "time": "1776274219582",
        //            "updateTime": "1776274219609",
        //            "orderListId": "-1"
        //        }
        //
        const info = order;
        const positionSide = this.safeString (order, 'positionSide');
        const defaultType = (positionSide !== undefined) ? 'swap' : 'spot';
        const marketId = this.safeString (order, 'symbol');
        market = this.safeMarket (marketId, market, undefined, defaultType);
        const side = this.safeStringLower (order, 'side');
        const timestamp = this.safeInteger (order, 'time');
        const statusId = this.safeStringUpper (order, 'status');
        const rawType = this.safeStringUpper (order, 'type');
        const stopPriceString = this.safeString (order, 'stopPrice');
        const triggerPrice = this.parseNumber (this.omitZero (stopPriceString));
        return this.safeOrder ({
            'info': info,
            'id': this.safeString (order, 'orderId'),
            'clientOrderId': this.safeString (order, 'clientOrderId'),
            'symbol': this.safeSymbol (marketId, market),
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'lastTradeTimestamp': undefined,
            'lastUpdateTimestamp': this.safeInteger (order, 'updateTime'),
            'type': this.parseOrderType (rawType),
            'timeInForce': this.safeString (order, 'timeInForce'),
            'postOnly': undefined,
            'side': side,
            'price': this.safeString (order, 'price'),
            'triggerPrice': triggerPrice,
            'average': this.safeString (order, 'avgPrice'),
            'cost': this.safeString (order, 'cumQuote'),
            'amount': this.safeString (order, 'origQty'),
            'filled': this.safeString (order, 'executedQty'),
            'remaining': undefined,
            'status': this.parseOrderStatus (statusId),
            'fee': undefined,
            'trades': undefined,
            'reduceOnly': this.safeBool2 (order, 'reduceOnly', 'ro'),
        }, market);
    }

    /**
     * @method
     * @name aster#fetchOrder
     * @description fetches information on an order made by the user
     * @see https://asterdex.github.io/aster-api-website/spot-v3/account%26trades/#query-order-user_data
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#query-order-user_data
     * @param {string} id the order id
     * @param {string} symbol unified symbol of the market the order was made in
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.clientOrderId] a unique id for the order
     * @returns {object} an [order structure]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async fetchOrder (id: string, symbol: Str = undefined, params = {}) {
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' fetchOrder() requires a symbol argument');
        }
        await this.loadMarketsAndSignIn ();
        const market = this.market (symbol);
        const request: Dict = {
            'symbol': market['id'],
        };
        const clientOrderId = this.safeString2 (params, 'clientOrderId', 'clientOid');
        params = this.omit (params, [ 'clientOrderId', 'clientOid' ]);
        if (clientOrderId !== undefined) {
            request['origClientOrderId'] = clientOrderId;
        } else {
            request['orderId'] = id;
        }
        let response: Dict;
        if (market['swap']) {
            response = await this.fapiPrivateGetV3Order (this.extend (request, params));
        } else {
            response = await this.sapiPrivateGetV3Order (this.extend (request, params));
        }
        //
        // SPOT & SWAP has similar formats
        //
        //    {
        //        "orderId": "17338441758",
        //        "symbol": "ETHUSDT",
        //        "status": "FILLED",
        //        "clientOrderId": "727Wt3TIUgkUCxXp20E543",
        //        "price": "0",
        //        "avgPrice": "2304.56000",
        //        "origQty": "0.010",
        //        "executedQty": "0.010",
        //        "cumQuote": "23.04560",
        //        "timeInForce": "GTC",
        //        "type": "MARKET",
        //        "side": "BUY",
        //        "stopPrice": "0",
        //        "origType": "MARKET",
        //        "time": "1776800300736",
        //        "updateTime": "1776800300700",
        //        "orderListId": "-1"                                   // only in SPOT
        //        "positionSide": "BOTH",                               // only in SWAP
        //        "reduceOnly": false,                                  // only in SWAP
        //        "closePosition": false,                               // only in SWAP
        //        "workingType": "CONTRACT_PRICE",                      // only in SWAP
        //        "priceProtect": false,                                // only in SWAP
        //        "newChainData": { "hash": "0x46aed5...67bdbec8ba" }   // only in SWAP
        //    }
        //
        return this.parseOrder (response, market);
    }

    /**
     * @method
     * @name aster#fetchOpenOrder
     * @description fetch an open order by the id
     * @see https://asterdex.github.io/aster-api-website/spot-v3/account%26trades/#query-current-open-order-user_data
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#query-current-open-order-user_data
     * @param {string} id order id
     * @param {string} symbol unified market symbol
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} an [order structure]{@link https://docs.ccxt.com/?id=order-structure}
     */
    async fetchOpenOrder (id: string, symbol: Str = undefined, params = {}): Promise<Order> {
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' fetchOpenOrder() requires a symbol argument');
        }
        await this.loadMarketsAndSignIn ();
        const market = this.market (symbol);
        const request: Dict = {
            'symbol': market['id'],
        };
        const clientOrderId = this.safeString2 (params, 'clientOrderId', 'clientOid');
        params = this.omit (params, [ 'clientOrderId', 'clientOid' ]);
        if (clientOrderId !== undefined) {
            request['origClientOrderId'] = clientOrderId;
        } else {
            request['orderId'] = id;
        }
        let response: Dict;
        if (market['spot']) {
            response = await this.sapiPrivateGetV3OpenOrder (this.extend (request, params));
        } else {
            response = await this.fapiPrivateGetV3OpenOrder (this.extend (request, params));
        }
        //
        // SPOT & SWAP has similar formats
        //
        //    {
        //        "orderId": "17338441758",
        //        "symbol": "ETHUSDT",
        //        "status": "FILLED",
        //        "clientOrderId": "727Wt3TIUgkUCxXp20E543",
        //        "price": "0",
        //        "avgPrice": "2304.56000",
        //        "origQty": "0.010",
        //        "executedQty": "0.010",
        //        "cumQuote": "23.04560",
        //        "timeInForce": "GTC",
        //        "type": "MARKET",
        //        "side": "BUY",
        //        "stopPrice": "0",
        //        "origType": "MARKET",
        //        "time": "1776800300736",
        //        "updateTime": "1776800300700",
        //        "orderListId": "-1"                                   // only in SPOT
        //        "positionSide": "BOTH",                               // only in SWAP
        //        "reduceOnly": false,                                  // only in SWAP
        //        "closePosition": false,                               // only in SWAP
        //        "workingType": "CONTRACT_PRICE",                      // only in SWAP
        //        "priceProtect": false,                                // only in SWAP
        //        "newChainData": { "hash": "0x46aed5...67bdbec8ba" }   // only in SWAP
        //    }
        //
        return this.parseOrder (response, market);
    }

    /**
     * @method
     * @name aster#fetchOrders
     * @description fetches information on multiple orders made by the user
     * @see https://asterdex.github.io/aster-api-website/spot-v3/account%26trades/#query-all-orders-user_data
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#all-orders-user_data
     * @param {string} symbol unified market symbol of the market orders were made in
     * @param {int} [since] the earliest time in ms to fetch orders for
     * @param {int} [limit] the maximum number of order structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.until] the latest time in ms to fetch orders for
     * @returns {Order[]} a list of [order structures]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async fetchOrders (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<Order[]> {
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' fetchOrders() requires a symbol argument');
        }
        await this.loadMarketsAndSignIn ();
        const market = this.market (symbol);
        let request: Dict = {
            'symbol': market['id'],
        };
        if (limit !== undefined) {
            request['limit'] = Math.min (limit, 1000);
        }
        if (since !== undefined) {
            request['startTime'] = since;
        }
        [ request, params ] = this.handleUntilOption ('endTime', request, params);
        let response: Dict;
        if (market['swap']) {
            response = await this.fapiPrivateGetV3AllOrders (this.extend (request, params));
        } else {
            response = await this.sapiPrivateGetV3AllOrders (this.extend (request, params));
        }
        //
        // SPOT & SWAP has similar responses
        //
        //    [
        //        {
        //            "orderId": "417594542",
        //            "symbol": "ETHUSDT",
        //            "status": "FILLED",
        //            "clientOrderId": "web_qnvMAhOJsiVbSyu0BdKG",
        //            "price": "0",                     // value set for unfilled
        //            "avgPrice": "2351.580000",        // value zero for unfilled
        //            "origQty": "0.0054",
        //            "executedQty": "0.0054",          // value zero for unfilled
        //            "cumQuote": "12.69853200",        // value zero for unfilled
        //            "timeInForce": "GTC",
        //            "type": "MARKET",
        //            "side": "SELL",
        //            "stopPrice": "0",
        //            "origType": "MARKET",
        //            "time": "1776274219582",
        //            "updateTime": "1776274219609",
        //            "orderListId": "-1",                                     // only in SPOT
        //            "reduceOnly": false,                                     // only in PERP
        //            "closePosition": false,                                  // only in PERP
        //            "positionSide": "BOTH",                                  // only in PERP
        //            "workingType": "CONTRACT_PRICE",                         // only in PERP
        //            "priceProtect": false,                                   // only in PERP
        //            "newChainData": { "hash": "0xe17d3d5b...dbca8b01" }      // only in PERP
        //        }, ...
        //
        return this.parseOrders (response, market, since, limit);
    }

    /**
     * @method
     * @name aster#fetchOpenOrders
     * @description fetch all unfilled currently open orders
     * @see https://asterdex.github.io/aster-api-website/spot-v3/account%26trades/#current-open-orders-user_data
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#current-all-open-orders-user_data
     * @param {string} symbol unified market symbol
     * @param {int} [since] the earliest time in ms to fetch open orders for
     * @param {int} [limit] the maximum number of  open orders structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.subType] "linear" or "inverse"
     * @param {string} [params.type] 'spot', 'option', use params["subType"] for swap and future markets
     * @returns {Order[]} a list of [order structures]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async fetchOpenOrders (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<Order[]> {
        await this.loadMarketsAndSignIn ();
        const request: Dict = {};
        let market: Market = undefined;
        let marketType: Str = undefined;
        if (symbol !== undefined) {
            market = this.market (symbol);
            request['symbol'] = market['id'];
        }
        if (symbol === undefined) {
            if (this.options['fetchOpenOrders']['warnIfNoSymbol']) {
                throw new ExchangeError (this.id + ' fetchOpenOrders(): WARNING - this method without providing "symbol" argument uses 40 times more rate-limit quota. If you acknowledge this warning, set ' + this.id + '.options["fetchOpenOrders"]["warnIfNoSymbol"] = false to suppress this warning message.');
            }
        } else {
            market = this.market (symbol);
            request['symbol'] = market['id'];
        }
        [ marketType, params ] = this.handleMarketTypeAndParams ('fetchOpenOrders', market, params);
        let subType: SubType = undefined;
        [ subType, params ] = this.handleSubTypeAndParams ('fetchOpenOrders', market, params);
        let response: NullableDict = undefined;
        if (this.isLinear (marketType, subType)) {
            response = await this.fapiPrivateGetV3OpenOrders (this.extend (request, params));
        } else if (marketType === 'spot') {
            response = await this.sapiPrivateGetV3OpenOrders (this.extend (request, params));
        }
        //
        // SPOT & SWAP has similar responses
        //
        //    [
        //        {
        //            "orderId": "17338239315",
        //            "symbol": "ETHUSDT",
        //            "status": "NEW",
        //            "clientOrderId": "web_AD_mbhgla7k15gptmwyr_x",
        //            "price": "2216.62",
        //            "avgPrice": "0",
        //            "origQty": "0.012",
        //            "executedQty": "0",
        //            "cumQuote": "0",
        //            "timeInForce": "GTC",
        //            "type": "LIMIT",
        //            "side": "BUY",
        //            "stopPrice": "0",
        //            "origType": "LIMIT",
        //            "time": "1776798208476",
        //            "updateTime": "1776798208450",
        //            "orderListId": "-1"                                   // only in SPOT
        //            "reduceOnly": false,                                  // only in PERP
        //            "closePosition": false,                               // only in PERP
        //            "positionSide": "BOTH",                               // only in PERP
        //            "workingType": "CONTRACT_PRICE",                      // only in PERP
        //            "priceProtect": false,                                // only in PERP
        //            "newChainData": { "hash": "0xf8a496....a7fd5" }       // only in PERP
        //        }
        //    ]
        //
        return this.parseOrders (response, market, since, limit);
    }

    /**
     * @method
     * @name aster#createOrder
     * @description create a trade order
     * @see https://asterdex.github.io/aster-api-website/spot-v3/account%26trades/#place-order-trade
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#new-order-trade
     * @param {string} symbol unified symbol of the market to create an order in
     * @param {string} type 'market' or 'limit' or 'STOP' or 'STOP_MARKET' or 'TAKE_PROFIT' or 'TAKE_PROFIT_MARKET' or 'TRAILING_STOP_MARKET'
     * @param {string} side 'buy' or 'sell'
     * @param {float} amount how much of you want to trade in units of the base currency
     * @param {float} [price] the price that the order is to be fulfilled, in units of the quote currency, ignored in market orders
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.reduceOnly] for swap and future reduceOnly is a string 'true' or 'false' that cant be sent with close position set to true or in hedge mode. For spot margin and option reduceOnly is a boolean.
     * @param {boolean} [params.test] whether to use the test endpoint or not, default is false
     * @param {float} [params.trailingPercent] the percent to trail away from the current market price
     * @param {float} [params.trailingTriggerPrice] the price to trigger a trailing order, default uses the price argument
     * @param {string} [params.positionSide] "BOTH" for one-way mode, "LONG" for buy side of hedged mode, "SHORT" for sell side of hedged mode
     * @param {float} [params.triggerPrice] the price that a trigger order is triggered at
     * @param {float} [params.stopLossPrice] the price that a stop loss order is triggered at
     * @param {float} [params.takeProfitPrice] the price that a take profit order is triggered at
     * @returns {object} an [order structure]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async createOrder (symbol: string, type: OrderType, side: OrderSide, amount: number, price: Num = undefined, params = {}) {
        await this.loadMarketsAndSignIn ();
        const market = this.market (symbol);
        const request = this.createOrderRequest (symbol, type, side, amount, price, params);
        let response: Dict;
        if (market['swap']) {
            response = await this.fapiPrivatePostV3Order (request);
        } else {
            response = await this.sapiPrivatePostV3Order (request);
        }
        //
        // SPOT & SWAP has similar responses
        //
        //    {
        //        "orderId": "17338441758",
        //        "symbol": "ETHUSDT",
        //        "status": "NEW",
        //        "clientOrderId": "727Wt3TIUgkUCxXp20E543",
        //        "price": "0",
        //        "avgPrice": "0.00000",
        //        "origQty": "0.010",
        //        "executedQty": "0",
        //        "cumQty": "0",
        //        "cumQuote": "0",
        //        "timeInForce": "GTC",
        //        "type": "MARKET",
        //        "side": "BUY",
        //        "stopPrice": "0",
        //        "origType": "MARKET",
        //        "time": "1776800300700",
        //        "updateTime": "1776800300700",
        //        "orderListId": "-1",                              // only in SPOT
        //        "workingType": "CONTRACT_PRICE",                  // only in PERP
        //        "positionSide": "BOTH",                           // only in PERP
        //        "reduceOnly": false,                              // only in PERP
        //        "closePosition": false,                           // only in PERP
        //        "priceProtect": false,                            // only in PERP
        //        "newChainData": { "hash": "0x46ae....c8ba" }      // only in PERP
        //    }
        //
        return this.parseOrder (response, market);
    }

    /**
     * @method
     * @name aster#createOrders
     * @description create a list of trade orders
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#new-order-trade
     * @param {Array} orders list of orders to create, each object should contain the parameters required by createOrder, namely symbol, type, side, amount, price and params
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} an [order structure]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async createOrders (orders: OrderRequest[], params = {}) {
        await this.loadMarketsAndSignIn ();
        const ordersRequests: List = [];
        let orderSymbols: List = [];
        if (orders.length > 5) {
            throw new InvalidOrder (this.id + ' createOrders() order list max 5 orders');
        }
        for (let i = 0; i < orders.length; i++) {
            const rawOrder = orders[i];
            const marketId = this.safeString (rawOrder, 'symbol');
            const currentMarket = this.market (marketId);
            orderSymbols.push (currentMarket['symbol']);
            const type = this.safeString (rawOrder, 'type');
            const side = this.safeString (rawOrder, 'side');
            const amount = this.safeValue (rawOrder, 'amount');
            const price = this.safeValue (rawOrder, 'price');
            const orderParams = this.safeDict (rawOrder, 'params', {});
            const orderRequest = this.createOrderRequest (marketId, type, side, amount, price, orderParams);
            ordersRequests.push (orderRequest);
        }
        orderSymbols = this.marketSymbols (orderSymbols, undefined, false, true, true);
        const market = this.market (orderSymbols[0]);
        if (market['spot']) {
            throw new NotSupported (this.id + ' createOrders() does not support ' + market['type'] + ' orders');
        }
        const request: Dict = {
            'batchOrders': ordersRequests,
        };
        const response = await this.fapiPrivatePostV3BatchOrders (this.extend (request, params));
        //
        //    [
        //        {
        //            "orderId": 17338699853,
        //            "symbol": "ETHUSDT",
        //            "status": "NEW",
        //            "clientOrderId": "NxMWPvOEyiF6TWh5UB8BQf0",
        //            "price": "0",
        //            "avgPrice": "0.00000",
        //            "origQty": "0.010",
        //            "executedQty": "0",
        //            "cumQty": "0",
        //            "cumQuote": "0",
        //            "timeInForce": "GTC",
        //            "type": "MARKET",
        //            "reduceOnly": false,
        //            "closePosition": false,
        //            "side": "BUY",
        //            "positionSide": "BOTH",
        //            "stopPrice": "0",
        //            "workingType": "CONTRACT_PRICE",
        //            "priceProtect": false,
        //            "origType": "MARKET",
        //            "updateTime": 1776802276050,
        //            "newChainData": {
        //                "hash": "0x5e569d9794cf726f72c2d000d401d20315e78e4df7b58023a489864624527dfe"
        //            }
        //        }
        //    ]
        //
        return this.parseOrders (response);
    }

    createOrderRequest (symbol: Str, type: Str, side: Str, amount: Num, price: Num = undefined, params = {}) {
        if (type === undefined) {
            throw new ArgumentsRequired (this.id + ' requires a type argument');
        }
        if (side === undefined) {
            throw new ArgumentsRequired (this.id + ' requires a side argument');
        }
        /**
         * @method
         * @ignore
         * @name aster#createOrderRequest
         * @description helper function to build the request
         * @param {string} symbol unified symbol of the market to create an order in
         * @param {string} type 'market' or 'limit'
         * @param {string} side 'buy' or 'sell'
         * @param {float} amount how much you want to trade in units of the base currency
         * @param {float} [price] the price that the order is to be fulfilled, in units of the quote currency, ignored in market orders
         * @param {object} [params] extra parameters specific to the exchange API endpoint
         * @returns {object} request to be sent to the exchange
         */
        const market = this.market (symbol);
        const initialUppercaseType = type.toUpperCase ();
        const isMarketOrder = initialUppercaseType === 'MARKET';
        const isLimitOrder = initialUppercaseType === 'LIMIT';
        const request: Dict = {
            'symbol': market['id'],
            'side': side.toUpperCase (),
        };
        const clientOrderId = this.safeString2 (params, 'newClientOrderId', 'clientOrderId');
        if (clientOrderId !== undefined) {
            request['newClientOrderId'] = clientOrderId;
        }
        const triggerPrice = this.safeString2 (params, 'triggerPrice', 'stopPrice');
        const stopLossPrice = this.safeString (params, 'stopLossPrice', triggerPrice);
        const takeProfitPrice = this.safeString (params, 'takeProfitPrice');
        const trailingDelta = this.safeString (params, 'trailingDelta');
        const trailingTriggerPrice = this.safeString2 (params, 'trailingTriggerPrice', 'activationPrice');
        const trailingPercent = this.safeStringN (params, [ 'trailingPercent', 'callbackRate', 'trailingDelta' ]);
        const isTrailingPercentOrder = trailingPercent !== undefined;
        const isStopLoss = stopLossPrice !== undefined || trailingDelta !== undefined;
        const isTakeProfit = takeProfitPrice !== undefined;
        let uppercaseType = initialUppercaseType;
        let stopPrice: Str = undefined;
        if (isTrailingPercentOrder) {
            if (market['swap']) {
                uppercaseType = 'TRAILING_STOP_MARKET';
                request['callbackRate'] = trailingPercent;
                if (trailingTriggerPrice !== undefined) {
                    request['activationPrice'] = this.priceToPrecision (symbol, trailingTriggerPrice);
                }
            }
        } else if (isStopLoss) {
            stopPrice = stopLossPrice;
            if (isMarketOrder) {
                uppercaseType = 'STOP_MARKET';
            } else if (isLimitOrder) {
                uppercaseType = 'STOP';
            }
        } else if (isTakeProfit) {
            stopPrice = takeProfitPrice;
            if (isMarketOrder) {
                uppercaseType = 'TAKE_PROFIT_MARKET';
            } else if (isLimitOrder) {
                uppercaseType = 'TAKE_PROFIT';
            }
        }
        const postOnly = this.isPostOnly (isMarketOrder, undefined, params);
        if (postOnly) {
            request['timeInForce'] = 'GTX';
        }
        //
        // spot
        // LIMIT timeInForce, quantity, price
        // MARKET quantity or quoteOrderQty
        // STOP and TAKE_PROFIT quantity, price, stopPrice
        // STOP_MARKET and TAKE_PROFIT_MARKET quantity, stopPrice
        // future
        // LIMIT timeInForce, quantity, price
        // MARKET quantity
        // STOP/TAKE_PROFIT quantity, price, stopPrice
        // STOP_MARKET/TAKE_PROFIT_MARKET stopPrice
        // TRAILING_STOP_MARKET callbackRate
        //
        // additional required fields depending on the order type
        const closePosition = this.safeBool (params, 'closePosition', false);
        let timeInForceIsRequired = false;
        let priceIsRequired = false;
        let triggerPriceIsRequired = false;
        let quantityIsRequired = false;
        request['type'] = uppercaseType;
        if (uppercaseType === 'MARKET') {
            if (market['spot']) {
                const quoteOrderQty = this.handleOption ('createOrder', 'quoteOrderQty', true);
                if (quoteOrderQty) {
                    const quoteOrderQtyNew = this.safeString2 (params, 'quoteOrderQty', 'cost');
                    const precision = market['precision']['price'];
                    if (quoteOrderQtyNew !== undefined) {
                        request['quoteOrderQty'] = this.decimalToPrecision (quoteOrderQtyNew, TRUNCATE, precision, this.precisionMode);
                    } else if (price !== undefined) {
                        const amountString = this.numberToString (amount);
                        const priceString = this.numberToString (price);
                        const quoteOrderQuantity = Precise.stringMul (amountString, priceString);
                        request['quoteOrderQty'] = this.decimalToPrecision (quoteOrderQuantity, TRUNCATE, precision, this.precisionMode);
                    } else {
                        quantityIsRequired = true;
                    }
                } else {
                    quantityIsRequired = true;
                }
            } else {
                quantityIsRequired = true;
            }
        } else if (uppercaseType === 'LIMIT') {
            timeInForceIsRequired = true;
            quantityIsRequired = true;
            priceIsRequired = true;
        } else if ((uppercaseType === 'STOP') || (uppercaseType === 'TAKE_PROFIT')) {
            quantityIsRequired = true;
            priceIsRequired = true;
            triggerPriceIsRequired = true;
        } else if ((uppercaseType === 'STOP_MARKET') || (uppercaseType === 'TAKE_PROFIT_MARKET')) {
            if (!closePosition) {
                quantityIsRequired = true;
            }
            triggerPriceIsRequired = true;
        } else if (uppercaseType === 'TRAILING_STOP_MARKET') {
            request['callbackRate'] = trailingPercent;
            if (trailingTriggerPrice !== undefined) {
                request['activationPrice'] = this.priceToPrecision (symbol, trailingTriggerPrice);
            }
        }
        if (quantityIsRequired) {
            const marketAmountPrecision = this.safeString (market['precision'], 'amount');
            const isPrecisionAvailable = (marketAmountPrecision !== undefined);
            if (isPrecisionAvailable) {
                request['quantity'] = this.amountToPrecision (symbol, amount);
            } else {
                request['quantity'] = this.parseToNumeric (amount);
            }
        }
        if (priceIsRequired) {
            if (price === undefined) {
                throw new InvalidOrder (this.id + ' createOrder() requires a price argument for a ' + type + ' order');
            }
            const pricePrecision = this.safeString (market['precision'], 'price');
            const isPricePrecisionAvailable = (pricePrecision !== undefined);
            if (isPricePrecisionAvailable) {
                request['price'] = this.priceToPrecision (symbol, price);
            } else {
                request['price'] = this.parseToNumeric (price);
            }
        }
        if (triggerPriceIsRequired) {
            if (stopPrice === undefined) {
                throw new InvalidOrder (this.id + ' createOrder() requires a stopPrice extra param for a ' + type + ' order');
            }
            if (stopPrice !== undefined) {
                request['stopPrice'] = this.priceToPrecision (symbol, stopPrice);
            }
        }
        if (timeInForceIsRequired && (this.safeString (params, 'timeInForce') === undefined) && (this.safeString (request, 'timeInForce') === undefined)) {
            let tif: Str = undefined;
            [ tif, params ] = this.handleOptionAndParams (params, 'createOrder', 'timeInForce');
            request['timeInForce'] = tif;
        }
        const requestParams = this.omit (params, [ 'newClientOrderId', 'clientOrderId', 'stopPrice', 'triggerPrice', 'trailingTriggerPrice', 'trailingPercent', 'trailingDelta', 'stopPrice', 'stopLossPrice', 'takeProfitPrice' ]);
        if (this.safeBool (this.options, 'builderFee') && market['swap']) {
            request['builder'] = this.safeString (this.options, 'builder');
            request['feeRate'] = this.safeString (this.options, 'builderRate');
        }
        return this.extend (request, requestParams);
    }

    /**
     * @method
     * @name aster#cancelAllOrders
     * @description cancel all open orders in a market
     * @see https://asterdex.github.io/aster-api-website/spot-v3/account%26trades/#cancel-all-open-orders-trade
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#current-all-open-orders-user_data
     * @param {string} symbol unified market symbol of the market to cancel orders in
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object[]} a list of [order structures]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async cancelAllOrders (symbol: Str = undefined, params = {}) {
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' cancelAllOrders() requires a symbol argument');
        }
        await this.loadMarketsAndSignIn ();
        const market = this.market (symbol);
        const request: Dict = {
            'symbol': market['id'],
        };
        let response: Dict;
        if (market['swap']) {
            response = await this.fapiPrivateDeleteV3AllOpenOrders (this.extend (request, params));
        } else {
            response = await this.sapiPrivateDeleteV3AllOpenOrders (this.extend (request, params));
        }
        //
        // SPOT & SWAP has same response
        //
        //     {
        //         "code": "200",
        //         "msg": "The operation of cancel all open order is done."
        //     }
        //
        return [
            this.safeOrder ({
                'info': response,
            }),
        ];
    }

    /**
     * @method
     * @name aster#cancelOrder
     * @description cancels an open order
     * @see https://asterdex.github.io/aster-api-website/spot-v3/account%26trades/#cancel-order-trade
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#cancel-order-trade
     * @param {string} id order id
     * @param {string} symbol unified symbol of the market the order was made in
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} An [order structure]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async cancelOrder (id: string, symbol: Str = undefined, params = {}) {
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' cancelOrder() requires a symbol argument');
        }
        await this.loadMarketsAndSignIn ();
        const market = this.market (symbol);
        const request: Dict = {
            'symbol': market['id'],
        };
        const clientOrderId = this.safeString2 (params, 'origClientOrderId', 'clientOrderId');
        if (clientOrderId !== undefined) {
            request['origClientOrderId'] = clientOrderId;
        } else {
            request['orderId'] = id;
        }
        params = this.omit (params, [ 'origClientOrderId', 'clientOrderId' ]);
        let response: Dict;
        if (market['swap']) {
            response = await this.fapiPrivateDeleteV3Order (this.extend (request, params));
        } else {
            response = await this.sapiPrivateDeleteV3Order (this.extend (request, params));
        }
        return this.parseOrder (response, market);
    }

    /**
     * @method
     * @name aster#cancelOrders
     * @description cancel multiple orders
     * @see https://asterdex.github.io/aster-api-website/spot-v3/account%26trades/#cancel-all-open-orders-trade
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#cancel-multiple-orders-trade
     * @param {string[]} ids order ids
     * @param {string} [symbol] unified market symbol
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     *
     * EXCHANGE SPECIFIC PARAMETERS
     * @param {string[]} [params.origClientOrderIdList] max length 10 e.g. ["my_id_1","my_id_2"], encode the double quotes. No space after comma
     * @param {int[]} [params.recvWindow]
     * @returns {object} an list of [order structures]{@link https://docs.ccxt.com/?id=order-structure}
     */
    override async cancelOrders (ids:string[], symbol: Str = undefined, params = {}) {
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' cancelOrders() requires a symbol argument');
        }
        await this.loadMarketsAndSignIn ();
        const market = this.market (symbol);
        const request: Dict = {
            'symbol': market['id'],
        };
        const clientOrderIdList = this.safeList (params, 'origClientOrderIdList');
        if (clientOrderIdList !== undefined) {
            request['origClientOrderIdList'] = clientOrderIdList;
        } else {
            request['orderIdList'] = ids;
        }
        let response: Dict;
        if (market['swap']) {
            response = await this.fapiPrivateDeleteV3BatchOrders (this.extend (request, params));
            //
            //    [
            //        {
            //            "clientOrderId": "myOrder1",
            //            "cumQty": "0",
            //            "cumQuote": "0",
            //            "executedQty": "0",
            //            "orderId": 283194212,
            //            "origQty": "11",
            //            "origType": "TRAILING_STOP_MARKET",
            //            "price": "0",
            //            "reduceOnly": false,
            //            "side": "BUY",
            //            "positionSide": "SHORT",
            //            "status": "CANCELED",
            //            "stopPrice": "9300",                  // please ignore when order type is TRAILING_STOP_MARKET
            //            "closePosition": false,               // if Close-All
            //            "symbol": "BTCUSDT",
            //            "timeInForce": "GTC",
            //            "type": "TRAILING_STOP_MARKET",
            //            "activatePrice": "9020",              // activation price, only return with TRAILING_STOP_MARKET order
            //            "priceRate": "0.3",                   // callback rate, only return with TRAILING_STOP_MARKET order
            //            "updateTime": 1571110484038,
            //            "workingType": "CONTRACT_PRICE",
            //            "priceProtect": false,                // if conditional order trigger is protected
            //        },
            //        {
            //            "code": -2011,
            //            "msg": "Unknown order sent."
            //        }
            //    ]
            //
        } else {
            response = await this.sapiPrivateDeleteV3AllOpenOrders (this.extend (request, params));
            //
            //  {"code": 200,"msg": "The operation of cancel all open order is done."}
            //
        }
        return this.parseOrders (response, market);
    }

    /**
     * @method
     * @name aster#setLeverage
     * @description set the level of leverage for a market
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#change-initial-leverage-trade
     * @param {float} leverage the rate of leverage
     * @param {string} symbol unified market symbol
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} response from the exchange
     */
    override async setLeverage (leverage: int, symbol: Str = undefined, params = {}) {
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' setLeverage() requires a symbol argument');
        }
        if ((leverage < 1) || (leverage > 125)) {
            throw new BadRequest (this.id + ' leverage should be between 1 and 125');
        }
        await this.loadMarketsAndSignIn ();
        const market = this.market (symbol);
        const request: Dict = {
            'symbol': market['id'],
            'leverage': leverage,
        };
        const response = await this.fapiPrivatePostV3Leverage (this.extend (request, params));
        //
        //     {
        //         "leverage": 21,
        //         "maxNotionalValue": "1000000",
        //         "symbol": "BTCUSDT"
        //     }
        //
        return response;
    }

    /**
     * @method
     * @name aster#fetchLeverages
     * @description fetch the set leverage for all markets
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#position-information-v3-user_data
     * @param {string[]} [symbols] a list of unified market symbols
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a list of [leverage structures]{@link https://docs.ccxt.com/?id=leverage-structure}
     */
    override async fetchLeverages (symbols: Strings = undefined, params = {}): Promise<Leverages> {
        await this.loadMarketsAndSignIn ();
        const response = await this.fapiPrivateGetV3PositionRisk (params);
        //
        //     [
        //         {
        //             "symbol": "INJUSDT",
        //             "positionAmt": "0.0",
        //             "entryPrice": "0.0",
        //             "markPrice": "0.00000000",
        //             "unRealizedProfit": "0.00000000",
        //             "liquidationPrice": "0",
        //             "leverage": "20",
        //             "maxNotionalValue": "25000",
        //             "marginType": "cross",
        //             "isolatedMargin": "0.00000000",
        //             "isAutoAddMargin": "false",
        //             "positionSide": "BOTH",
        //             "notional": "0",
        //             "isolatedWallet": "0",
        //             "updateTime": 0
        //         }
        //     ]
        //
        return this.parseLeverages (this.toArray (response), symbols, 'symbol');
    }

    override parseLeverage (leverage: Dict, market: Market = undefined): Leverage {
        //
        //     {
        //         "symbol": "INJUSDT",
        //         "positionAmt": "0.0",
        //         "entryPrice": "0.0",
        //         "markPrice": "0.00000000",
        //         "unRealizedProfit": "0.00000000",
        //         "liquidationPrice": "0",
        //         "leverage": "20",
        //         "maxNotionalValue": "25000",
        //         "marginType": "cross",
        //         "isolatedMargin": "0.00000000",
        //         "isAutoAddMargin": "false",
        //         "positionSide": "BOTH",
        //         "notional": "0",
        //         "isolatedWallet": "0",
        //         "updateTime": 0
        //     }
        //
        const marketId = this.safeString (leverage, 'symbol');
        const marginMode = this.safeStringLower (leverage, 'marginType');
        const side = this.safeStringLower (leverage, 'positionSide');
        let longLeverage: Int = undefined;
        let shortLeverage: Int = undefined;
        const leverageValue = this.safeInteger (leverage, 'leverage');
        if ((side === undefined) || (side === 'both')) {
            longLeverage = leverageValue;
            shortLeverage = leverageValue;
        } else if (side === 'long') {
            longLeverage = leverageValue;
        } else if (side === 'short') {
            shortLeverage = leverageValue;
        }
        return {
            'info': leverage,
            'symbol': this.safeSymbol (marketId, market),
            'marginMode': marginMode,
            'longLeverage': longLeverage,
            'shortLeverage': shortLeverage,
        } as Leverage;
    }

    /**
     * @method
     * @name aster#fetchMarginModes
     * @description fetches margin mode of the user
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#position-information-v3-user_data
     * @param {string[]} symbols unified market symbols
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a list of [margin mode structures]{@link https://docs.ccxt.com/?id=margin-mode-structure}
     */
    override async fetchMarginModes (symbols: Strings = undefined, params = {}): Promise<MarginModes> {
        await this.loadMarketsAndSignIn ();
        const response = await this.fapiPrivateGetV3PositionRisk (params);
        //
        //
        //     [
        //         {
        //             "symbol": "INJUSDT",
        //             "positionAmt": "0.0",
        //             "entryPrice": "0.0",
        //             "markPrice": "0.00000000",
        //             "unRealizedProfit": "0.00000000",
        //             "liquidationPrice": "0",
        //             "leverage": "20",
        //             "maxNotionalValue": "25000",
        //             "marginType": "cross",
        //             "isolatedMargin": "0.00000000",
        //             "isAutoAddMargin": "false",
        //             "positionSide": "BOTH",
        //             "notional": "0",
        //             "isolatedWallet": "0",
        //             "updateTime": 0
        //         }
        //     ]
        //
        //
        return this.parseMarginModes (this.toArray (response), symbols, 'symbol', 'swap');
    }

    override parseMarginMode (marginMode: Dict, market: Market = undefined): MarginMode {
        //
        //     {
        //         "symbol": "INJUSDT",
        //         "positionAmt": "0.0",
        //         "entryPrice": "0.0",
        //         "markPrice": "0.00000000",
        //         "unRealizedProfit": "0.00000000",
        //         "liquidationPrice": "0",
        //         "leverage": "20",
        //         "maxNotionalValue": "25000",
        //         "marginType": "cross",
        //         "isolatedMargin": "0.00000000",
        //         "isAutoAddMargin": "false",
        //         "positionSide": "BOTH",
        //         "notional": "0",
        //         "isolatedWallet": "0",
        //         "updateTime": 0
        //     }
        //
        const marketId = this.safeString (marginMode, 'symbol');
        market = this.safeMarket (marketId, market, undefined, 'swap');
        return {
            'info': marginMode,
            'symbol': this.safeString (market, 'symbol'),
            'marginMode': this.safeStringLower (marginMode, 'marginType'),
        } as MarginMode;
    }

    /**
     * @method
     * @name aster#fetchMarginAdjustmentHistory
     * @description fetches the history of margin added or reduced from contract isolated positions
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#get-position-margin-change-history-trade
     * @param {string} symbol unified market symbol
     * @param {string} [type] "add" or "reduce"
     * @param {int} [since] timestamp in ms of the earliest change to fetch
     * @param {int} [limit] the maximum amount of changes to fetch
     * @param {object} params extra parameters specific to the exchange API endpoint
     * @param {int} [params.until] timestamp in ms of the latest change to fetch
     * @returns {object[]} a list of [margin structures]{@link https://docs.ccxt.com/?id=margin-loan-structure}
     */
    override async fetchMarginAdjustmentHistory (symbol: Str = undefined, type: Str = undefined, since: Num = undefined, limit: Num = undefined, params = {}): Promise<MarginModification[]> {
        if (symbol === undefined) {
            throw new ArgumentsRequired (this.id + ' fetchMarginAdjustmentHistory () requires a symbol argument');
        }
        await this.loadMarketsAndSignIn ();
        const market = this.market (symbol);
        const until = this.safeInteger (params, 'until');
        params = this.omit (params, 'until');
        const request: Dict = {
            'symbol': market['id'],
        };
        if (type !== undefined) {
            request['type'] = (type === 'add') ? 1 : 2;
        }
        if (limit !== undefined) {
            request['limit'] = Math.min (limit, 1000);
        }
        if (since !== undefined) {
            request['startTime'] = since;
        }
        if (until !== undefined) {
            request['endTime'] = until;
        }
        const response = await this.fapiPrivateGetV3PositionMarginHistory (this.extend (request, params));
        //
        //     [
        //         {
        //             "amount": "23.36332311",
        //             "asset": "USDT",
        //             "symbol": "BTCUSDT",
        //             "time": 1578047897182,
        //             "type": 1,
        //             "positionSide": "BOTH"
        //         }
        //     ]
        //
        const modifications = this.parseMarginModifications (this.toArray (response));
        return this.filterBySymbolSinceLimit (modifications, symbol, since, limit);
    }

    override parseMarginModification (data: Dict, market: Market = undefined): MarginModification {
        //
        //     {
        //         "amount": "100",
        //         "asset": "USDT",
        //         "symbol": "BTCUSDT",
        //         "time": 1578047900425,
        //         "type": 1,
        //         "positionSide": "LONG"
        //     }
        //
        //     {
        //         "amount": 100.0,
        //         "code": 200,
        //         "msg": "Successfully modify position margin.",
        //         "type": 1
        //     }
        //
        const rawType = this.safeInteger (data, 'type');
        const errorCode = this.safeString (data, 'code');
        const marketId = this.safeString (data, 'symbol');
        const timestamp = this.safeInteger (data, 'time');
        market = this.safeMarket (marketId, market, undefined, 'swap');
        const noErrorCode = errorCode === undefined;
        const success = errorCode === '200';
        return {
            'info': data,
            'symbol': market['symbol'],
            'type': (rawType === 1) ? 'add' : 'reduce',
            'marginMode': 'isolated',
            'amount': this.safeNumber (data, 'amount'),
            'code': this.safeString (data, 'asset'),
            'total': undefined,
            'status': (success || noErrorCode) ? 'ok' : 'failed',
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
        };
    }

    async modifyMarginHelper (symbol: string, amount: any, addOrReduce: any, params = {}) {
        await this.loadMarketsAndSignIn ();
        const market = this.market (symbol);
        amount = this.amountToPrecision (symbol, amount);
        const request: Dict = {
            'type': addOrReduce,
            'symbol': market['id'],
            'amount': amount,
        };
        const code = market['quote'];
        const response = await this.fapiPrivatePostV3PositionMargin (this.extend (request, params));
        //
        //     {
        //         "amount": 100.0,
        //         "code": 200,
        //         "msg": "Successfully modify position margin.",
        //         "type": 1
        //     }
        //
        return this.extend (this.parseMarginModification (response, market), { 'code': code });
    }

    /**
     * @method
     * @name aster#reduceMargin
     * @description remove margin from a position
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#modify-isolated-position-margin-trade
     * @param {string} symbol unified market symbol
     * @param {float} amount the amount of margin to remove
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [margin structure]{@link https://docs.ccxt.com/?id=reduce-margin-structure}
     */
    override async reduceMargin (symbol: string, amount: number, params = {}): Promise<MarginModification> {
        return await this.modifyMarginHelper (symbol, amount, 2, params);
    }

    /**
     * @method
     * @name aster#addMargin
     * @description add margin
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#modify-isolated-position-margin-trade
     * @param {string} symbol unified market symbol
     * @param {float} amount amount of margin to add
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [margin structure]{@link https://docs.ccxt.com/?id=add-margin-structure}
     */
    override async addMargin (symbol: string, amount: number, params = {}): Promise<MarginModification> {
        return await this.modifyMarginHelper (symbol, amount, 1, params);
    }

    override parseIncome (income: any, market: Market = undefined) {
        //
        //     {
        //       "symbol": "ETHUSDT",
        //       "incomeType": "FUNDING_FEE",
        //       "income": "0.00134317",
        //       "asset": "USDT",
        //       "time": "1621584000000",
        //       "info": "FUNDING_FEE",
        //       "tranId": "4480321991774044580",
        //       "tradeId": ""
        //     }
        //
        const marketId = this.safeString (income, 'symbol');
        const currencyId = this.safeString (income, 'asset');
        const timestamp = this.safeInteger (income, 'time');
        return {
            'info': income,
            'symbol': this.safeSymbol (marketId, market, undefined, 'swap'),
            'code': this.safeCurrencyCode (currencyId),
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'id': this.safeString (income, 'tranId'),
            'amount': this.safeNumber (income, 'income'),
        };
    }

    /**
     * @method
     * @name aster#fetchFundingHistory
     * @description fetch the history of funding payments paid and received on this account
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#get-income-historyuser_data
     * @param {string} symbol unified market symbol
     * @param {int} [since] the earliest time in ms to fetch funding history for
     * @param {int} [limit] the maximum number of funding history structures to retrieve
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.until] timestamp in ms of the latest funding history entry
     * @param {boolean} [params.portfolioMargin] set to true if you would like to fetch the funding history for a portfolio margin account
     * @param {string} [params.subType] "linear" or "inverse"
     * @returns {object} a [funding history structure]{@link https://docs.ccxt.com/?id=funding-history-structure}
     */
    override async fetchFundingHistory (symbol: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}) {
        await this.loadMarketsAndSignIn ();
        let market: Market = undefined;
        let request: Dict = {
            'incomeType': 'FUNDING_FEE', // "TRANSFER"，"WELCOME_BONUS", "REALIZED_PNL"，"FUNDING_FEE", "COMMISSION", "INSURANCE_CLEAR", and "MARKET_MERCHANT_RETURN_REWARD"
        };
        if (symbol !== undefined) {
            market = this.market (symbol);
            request['symbol'] = market['id'];
        }
        [ request, params ] = this.handleUntilOption ('endTime', request, params);
        if (since !== undefined) {
            request['startTime'] = since;
        }
        if (limit !== undefined) {
            request['limit'] = Math.min (limit, 1000); // max 1000
        }
        const response = await this.fapiPrivateGetV3Income (this.extend (request, params));
        return this.parseIncomes (response, market, since, limit);
    }

    override parseLedgerEntry (item: Dict, currency: Currency = undefined): LedgerEntry {
        //
        //     {
        //         "symbol": "",
        //         "incomeType": "TRANSFER",
        //         "income": "10.00000000",
        //         "asset": "USDT",
        //         "time": 1677645250000,
        //         "info": "TRANSFER",
        //         "tranId": 131001573082,
        //         "tradeId": ""
        //     }
        //
        let amount = this.safeString (item, 'income');
        let direction: Str = undefined;
        if (Precise.stringLe (amount, '0')) {
            direction = 'out';
            amount = Precise.stringMul ('-1', amount);
        } else {
            direction = 'in';
        }
        const currencyId = this.safeString (item, 'asset');
        const code = this.safeCurrencyCode (currencyId, currency);
        currency = this.safeCurrency (currencyId, currency);
        const timestamp = this.safeInteger (item, 'time');
        const type = this.safeString (item, 'incomeType');
        return this.safeLedgerEntry ({
            'info': item,
            'id': this.safeString (item, 'tranId'),
            'direction': direction,
            'account': undefined,
            'referenceAccount': undefined,
            'referenceId': this.safeString (item, 'tradeId'),
            'type': this.parseLedgerEntryType (type),
            'currency': code,
            'amount': this.parseNumber (amount),
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'before': undefined,
            'after': undefined,
            'status': undefined,
            'fee': undefined,
        }, currency) as LedgerEntry;
    }

    parseLedgerEntryType (type: any) {
        const ledgerType: Dict = {
            'TRANSFER': 'transfer',
            'WELCOME_BONUS': 'cashback',
            'REALIZED_PNL': 'trade',
            'FUNDING_FEE': 'fee',
            'COMMISSION': 'commission',
            'INSURANCE_CLEAR': 'settlement',
            'MARKET_MERCHANT_RETURN_REWARD': 'cashback',
        };
        return this.safeString (ledgerType, (type as string), type);
    }

    /**
     * @method
     * @name aster#fetchLedger
     * @description fetch the history of changes, actions done by the user or operations that altered the balance of the user
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#get-income-historyuser_data
     * @param {string} [code] unified currency code
     * @param {int} [since] timestamp in ms of the earliest ledger entry
     * @param {int} [limit] max number of ledger entries to return
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {int} [params.until] timestamp in ms of the latest ledger entry
     * @returns {object} a [ledger structure]{@link https://docs.ccxt.com/?id=ledger}
     */
    override async fetchLedger (code: Str = undefined, since: Int = undefined, limit: Int = undefined, params = {}): Promise<LedgerEntry[]> {
        await this.loadMarketsAndSignIn ();
        let currency: Currency = undefined;
        if (code !== undefined) {
            currency = this.currency (code);
        }
        const request: Dict = {};
        if (since !== undefined) {
            request['startTime'] = since;
        }
        if (limit !== undefined) {
            request['limit'] = Math.min (limit, 1000); // max 1000
        }
        const until = this.safeInteger (params, 'until');
        if (until !== undefined) {
            params = this.omit (params, 'until');
            request['endTime'] = until;
        }
        const response = await this.fapiPrivateGetV3Income (this.extend (request, params));
        //
        //     [
        //         {
        //             "symbol": "",
        //             "incomeType": "TRANSFER",
        //             "income": "10.00000000",
        //             "asset": "USDT",
        //             "time": 1677645250000,
        //             "info": "TRANSFER",
        //             "tranId": 131001573082,
        //             "tradeId": ""
        //         }
        //     ]
        //
        return this.parseLedger (response, currency, since, limit);
    }

    parsePositionRisk (position: any, market: Market = undefined) {
        //
        //     {
        //         "entryPrice": "6563.66500",
        //         "marginType": "isolated",
        //         "isAutoAddMargin": "false",
        //         "isolatedMargin": "15517.54150468",
        //         "leverage": "10",
        //         "liquidationPrice": "5930.78",
        //         "markPrice": "6679.50671178",
        //         "maxNotionalValue": "20000000",
        //         "positionSide": "LONG",
        //         "positionAmt": "20.000",
        //         "symbol": "BTCUSDT",
        //         "unRealizedProfit": "2316.83423560",
        //         "updateTime": 1625474304765
        //     }
        //
        const marketId = this.safeString (position, 'symbol');
        market = this.safeMarket (marketId, market, undefined, 'contract');
        const symbol = this.safeString (market, 'symbol');
        const isolatedMarginString = this.safeString (position, 'isolatedMargin');
        const leverageBrackets = this.safeDict (this.options, 'leverageBrackets', {});
        const leverageBracket = this.safeList (leverageBrackets, symbol, []);
        const notionalString = this.safeString2 (position, 'notional', 'notionalValue');
        const notionalStringAbs = Precise.stringAbs (notionalString);
        let maintenanceMarginPercentageString: Str = undefined;
        for (let i = 0; i < leverageBracket.length; i++) {
            const bracket = leverageBracket[i];
            if (Precise.stringLt (notionalStringAbs, bracket[0])) {
                break;
            }
            maintenanceMarginPercentageString = bracket[1];
        }
        const notional = this.parseNumber (notionalStringAbs);
        const contractsAbs = Precise.stringAbs (this.safeString (position, 'positionAmt'));
        const contracts = this.parseNumber (contractsAbs);
        const unrealizedPnlString = this.safeString (position, 'unRealizedProfit');
        const unrealizedPnl = this.parseNumber (unrealizedPnlString);
        const liquidationPriceString = this.omitZero (this.safeString (position, 'liquidationPrice'));
        const liquidationPrice = this.parseNumber (liquidationPriceString);
        let collateralString: Str = undefined;
        let marginMode = this.safeString (position, 'marginType');
        if (marginMode === undefined && isolatedMarginString !== undefined) {
            marginMode = Precise.stringEq (isolatedMarginString, '0') ? 'cross' : 'isolated';
        }
        let side: Str = undefined;
        if (Precise.stringGt (notionalString, '0')) {
            side = 'long';
        } else if (Precise.stringLt (notionalString, '0')) {
            side = 'short';
        }
        const entryPriceString = this.safeString (position, 'entryPrice');
        const entryPrice = this.parseNumber (entryPriceString);
        const contractSize = this.safeValue (market, 'contractSize');
        const contractSizeString = this.numberToString (contractSize);
        // as oppose to notionalValue
        const linear = ('notional' in position);
        if (marginMode === 'cross') {
            // calculate collateral
            const precision = this.safeDict (market, 'precision', {});
            const basePrecisionValue = this.safeString (precision, 'base');
            const quotePrecisionValue = this.safeString2 (precision, 'quote', 'price');
            const precisionIsUndefined = (basePrecisionValue === undefined) && (quotePrecisionValue === undefined);
            if (!precisionIsUndefined) {
                if (linear) {
                    // walletBalance = (liquidationPrice * (±1 + mmp) ± entryPrice) * contracts
                    let onePlusMaintenanceMarginPercentageString: Str = undefined;
                    let entryPriceSignString = entryPriceString;
                    if (side === 'short') {
                        onePlusMaintenanceMarginPercentageString = Precise.stringAdd ('1', maintenanceMarginPercentageString);
                        entryPriceSignString = Precise.stringMul ('-1', entryPriceSignString);
                    } else {
                        onePlusMaintenanceMarginPercentageString = Precise.stringAdd ('-1', maintenanceMarginPercentageString);
                    }
                    const inner = Precise.stringMul (liquidationPriceString, onePlusMaintenanceMarginPercentageString);
                    const leftSide = Precise.stringAdd (inner, entryPriceSignString);
                    const quotePrecision = this.precisionFromString (this.safeString2 (precision, 'quote', 'price'));
                    if (quotePrecision !== undefined) {
                        collateralString = Precise.stringDiv (Precise.stringMul (leftSide, contractsAbs), '1', quotePrecision);
                    }
                } else {
                    // walletBalance = (contracts * contractSize) * (±1/entryPrice - (±1 - mmp) / liquidationPrice)
                    let onePlusMaintenanceMarginPercentageString: Str = undefined;
                    let entryPriceSignString = entryPriceString;
                    if (side === 'short') {
                        onePlusMaintenanceMarginPercentageString = Precise.stringSub ('1', maintenanceMarginPercentageString);
                    } else {
                        onePlusMaintenanceMarginPercentageString = Precise.stringSub ('-1', maintenanceMarginPercentageString);
                        entryPriceSignString = Precise.stringMul ('-1', entryPriceSignString);
                    }
                    const leftSide = Precise.stringMul (contractsAbs, contractSizeString);
                    const rightSide = Precise.stringSub (Precise.stringDiv ('1', entryPriceSignString), Precise.stringDiv (onePlusMaintenanceMarginPercentageString, liquidationPriceString));
                    const basePrecision = this.precisionFromString (this.safeString (precision, 'base'));
                    if (basePrecision !== undefined) {
                        collateralString = Precise.stringDiv (Precise.stringMul (leftSide, rightSide), '1', basePrecision);
                    }
                }
            }
        } else {
            collateralString = this.safeString (position, 'isolatedMargin');
        }
        collateralString = (collateralString === undefined) ? '0' : collateralString;
        const collateral = this.parseNumber (collateralString);
        const markPrice = this.parseNumber (this.omitZero (this.safeString (position, 'markPrice')));
        let timestamp = this.safeInteger (position, 'updateTime');
        if (timestamp === 0) {
            timestamp = undefined;
        }
        const maintenanceMarginPercentage = this.parseNumber (maintenanceMarginPercentageString);
        let maintenanceMarginString = Precise.stringMul (maintenanceMarginPercentageString, notionalStringAbs);
        if (maintenanceMarginString === undefined) {
            // for a while, this new value was a backup to the existing calculations, but in future we might prioritize this
            maintenanceMarginString = this.safeString (position, 'maintMargin');
        }
        const maintenanceMargin = this.parseNumber (maintenanceMarginString);
        let initialMarginString: Str = undefined;
        let initialMarginPercentageString: Str = undefined;
        const leverageString = this.safeString (position, 'leverage');
        if (leverageString !== undefined) {
            const leverage = parseInt (leverageString);
            const rational = this.isRoundNumber (1000 % leverage);
            initialMarginPercentageString = Precise.stringDiv ('1', leverageString, 8);
            if (!rational) {
                initialMarginPercentageString = Precise.stringAdd (initialMarginPercentageString, '1e-8');
            }
            const unrounded = Precise.stringMul (notionalStringAbs, initialMarginPercentageString);
            initialMarginString = Precise.stringDiv (unrounded, '1', 8);
        } else {
            initialMarginString = this.safeString (position, 'initialMargin');
            const unrounded = Precise.stringMul (initialMarginString, '1');
            initialMarginPercentageString = Precise.stringDiv (unrounded, notionalStringAbs, 8);
        }
        let marginRatio: Num = undefined;
        let percentage: Num = undefined;
        if (!Precise.stringEquals (collateralString, '0')) {
            marginRatio = this.parseNumber (Precise.stringDiv (Precise.stringAdd (Precise.stringDiv (maintenanceMarginString, collateralString), '5e-5'), '1', 4));
            percentage = this.parseNumber (Precise.stringMul (Precise.stringDiv (unrealizedPnlString, initialMarginString, 4), '100'));
        }
        const positionSide = this.safeString (position, 'positionSide');
        const hedged = positionSide !== 'BOTH';
        return this.safePosition ({
            'info': position,
            'id': undefined,
            'symbol': symbol,
            'contracts': contracts,
            'contractSize': contractSize,
            'unrealizedPnl': unrealizedPnl,
            'leverage': this.parseNumber (leverageString),
            'liquidationPrice': liquidationPrice,
            'collateral': collateral,
            'notional': notional,
            'markPrice': markPrice,
            'entryPrice': entryPrice,
            'timestamp': timestamp,
            'initialMargin': this.parseNumber (initialMarginString),
            'initialMarginPercentage': this.parseNumber (initialMarginPercentageString),
            'maintenanceMargin': maintenanceMargin,
            'maintenanceMarginPercentage': maintenanceMarginPercentage,
            'marginRatio': marginRatio,
            'datetime': this.iso8601 (timestamp),
            'marginMode': marginMode,
            'side': side,
            'hedged': hedged,
            'percentage': percentage,
            'stopLossPrice': undefined,
            'takeProfitPrice': undefined,
        });
    }

    /**
     * @method
     * @name aster#fetchPositionsRisk
     * @description fetch positions risk
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#position-information-v3-user_data
     * @param {string[]|undefined} symbols list of unified market symbols
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} data on the positions risk
     */
    override async fetchPositionsRisk (symbols: Strings = undefined, params = {}) {
        if (symbols !== undefined) {
            if (!Array.isArray (symbols)) {
                throw new ArgumentsRequired (this.id + ' fetchPositionsRisk() requires an array argument for symbols');
            }
        }
        await this.loadMarketsAndSignIn ();
        await this.loadLeverageBrackets (false, params);
        const request: Dict = {};
        const response = await this.fapiPrivateGetV3PositionRisk (this.extend (request, params));
        //
        //     [
        //         {
        //             "entryPrice": "6563.66500",
        //             "marginType": "isolated",
        //             "isAutoAddMargin": "false",
        //             "isolatedMargin": "15517.54150468",
        //             "leverage": "10",
        //             "liquidationPrice": "5930.78",
        //             "markPrice": "6679.50671178",
        //             "maxNotionalValue": "20000000",
        //             "positionSide": "LONG",
        //             "positionAmt": "20.000", // negative value for 'SHORT'
        //             "symbol": "BTCUSDT",
        //             "unRealizedProfit": "2316.83423560",
        //             "updateTime": 1625474304765
        //         }
        //     ]
        //
        const rawPositions = this.toArray (response);
        const result: List = [];
        for (let i = 0; i < rawPositions.length; i++) {
            const rawPosition = rawPositions[i];
            const entryPriceString = this.safeString (rawPosition, 'entryPrice');
            if (Precise.stringGt (entryPriceString, '0')) {
                result.push (this.parsePositionRisk (rawPosition));
            }
        }
        symbols = this.marketSymbols (symbols);
        return this.filterByArrayPositions (result, 'symbol', symbols, false);
    }

    /**
     * @method
     * @name aster#fetchPositions
     * @description fetch all open positions
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#position-information-v3-user_data
     * @param {string[]} [symbols] list of unified market symbols
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @param {string} [params.method] method name to call, "positionRisk", "account" or "option", default is "positionRisk"
     * @returns {object[]} a list of [position structure]{@link https://docs.ccxt.com/?id=position-structure}
     */
    override async fetchPositions (symbols: Strings = undefined, params = {}): Promise<Position[]> {
        let defaultMethod: Str = undefined;
        [ defaultMethod, params ] = this.handleOptionAndParams (params, 'fetchPositions', 'method');
        if (defaultMethod === undefined) {
            const options = this.safeDict (this.options, 'fetchPositions');
            if (options === undefined) {
                defaultMethod = this.safeString (this.options, 'fetchPositions', 'positionRisk');
            } else {
                defaultMethod = 'positionRisk';
            }
        }
        if (defaultMethod === 'positionRisk') {
            return await this.fetchPositionsRisk (symbols, params);
        } else if (defaultMethod === 'account') {
            return await this.fetchAccountPositions (symbols, params);
        } else {
            throw new NotSupported (this.id + '.options["fetchPositions"]["method"] or params["method"] = "' + defaultMethod + '" is invalid, please choose between "account" and "positionRisk"');
        }
    }

    parseAccountPositions (account: any, filterClosed = false) {
        const positions = this.safeList (account, 'positions', []);
        const assets = this.safeList (account, 'assets', []);
        const balances: Dict = {};
        for (let i = 0; i < assets.length; i++) {
            const entry = assets[i];
            const currencyId = this.safeString (entry, 'asset');
            const code = this.safeCurrencyCode (currencyId);
            const crossWalletBalance = this.safeString (entry, 'crossWalletBalance');
            const crossUnPnl = this.safeString (entry, 'crossUnPnl');
            if (code !== undefined) {
                balances[code] = {
                    'crossMargin': Precise.stringAdd (crossWalletBalance, crossUnPnl),
                    'crossWalletBalance': crossWalletBalance,
                };
            }
        }
        const result: List = [];
        for (let i = 0; i < positions.length; i++) {
            const position = positions[i];
            const marketId = this.safeString (position, 'symbol');
            const market = this.safeMarket (marketId, undefined, undefined, 'contract');
            const code = market['linear'] ? market['quote'] : market['base'];
            const maintenanceMargin = this.safeString (position, 'maintMargin');
            // check for maintenance margin so empty positions are not returned
            const isPositionOpen = (maintenanceMargin !== '0') && (maintenanceMargin !== '0.00000000');
            if (!filterClosed || isPositionOpen) {
                // sometimes not all the codes are correctly returned...
                if (code in balances) {
                    const parsed = this.parseAccountPosition (this.extend (position, {
                        'crossMargin': balances[code]['crossMargin'],
                        'crossWalletBalance': balances[code]['crossWalletBalance'],
                    }), market);
                    result.push (parsed);
                }
            }
        }
        return result;
    }

    parseAccountPosition (position: any, market: Market = undefined) {
        const marketId = this.safeString (position, 'symbol');
        market = this.safeMarket (marketId, market, undefined, 'contract');
        const symbol = this.safeString (market, 'symbol');
        const leverageString = this.safeString (position, 'leverage');
        const leverage = (leverageString !== undefined) ? parseInt (leverageString) : undefined;
        const initialMarginString = this.safeString (position, 'initialMargin');
        const initialMargin = this.parseNumber (initialMarginString);
        let initialMarginPercentageString: Str = undefined;
        if (leverageString !== undefined) {
            initialMarginPercentageString = Precise.stringDiv ('1', leverageString, 8);
            if (leverage === undefined) {
                throw new ExchangeError (this.id + ' parseAccountPosition() missing leverage');
            }
            const rational = this.isRoundNumber (1000 % leverage);
            if (!rational) {
                initialMarginPercentageString = Precise.stringDiv (Precise.stringAdd (initialMarginPercentageString, '1e-8'), '1', 8);
            }
        }
        // as oppose to notionalValue
        const usdm = ('notional' in position);
        const maintenanceMarginString = this.safeString (position, 'maintMargin');
        const maintenanceMargin = this.parseNumber (maintenanceMarginString);
        const entryPriceString = this.safeString (position, 'entryPrice');
        let entryPrice: Num = this.parseNumber (entryPriceString);
        const notionalString = this.safeString2 (position, 'notional', 'notionalValue');
        const notionalStringAbs = Precise.stringAbs (notionalString);
        const notional = this.parseNumber (notionalStringAbs);
        let contractsString = this.safeString (position, 'positionAmt');
        let contractsStringAbs = Precise.stringAbs (contractsString);
        if (contractsString === undefined) {
            const entryNotional = Precise.stringMul (Precise.stringMul (leverageString, initialMarginString), entryPriceString);
            const contractSizeNew = this.safeString (market, 'contractSize');
            contractsString = Precise.stringDiv (entryNotional, contractSizeNew);
            contractsStringAbs = Precise.stringDiv (Precise.stringAdd (contractsString, '0.5'), '1', 0);
        }
        const contracts = this.parseNumber (contractsStringAbs);
        const leverageBrackets = this.safeDict (this.options, 'leverageBrackets', {});
        const leverageBracket = this.safeList (leverageBrackets, symbol, []);
        let maintenanceMarginPercentageString: Str = undefined;
        for (let i = 0; i < leverageBracket.length; i++) {
            const bracket = leverageBracket[i];
            if (Precise.stringLt (notionalStringAbs, bracket[0])) {
                break;
            }
            maintenanceMarginPercentageString = bracket[1];
        }
        const maintenanceMarginPercentage = this.parseNumber (maintenanceMarginPercentageString);
        const unrealizedPnlString = this.safeString (position, 'unrealizedProfit');
        const unrealizedPnl = this.parseNumber (unrealizedPnlString);
        let timestamp = this.safeInteger (position, 'updateTime');
        if (timestamp === 0) {
            timestamp = undefined;
        }
        let isolated = this.safeBool (position, 'isolated');
        if (isolated === undefined) {
            const isolatedMarginRaw = this.safeString (position, 'isolatedMargin');
            isolated = !Precise.stringEq (isolatedMarginRaw, '0');
        }
        let marginMode: Str = undefined;
        let collateralString: Str = undefined;
        let walletBalance: Str = undefined;
        if (isolated) {
            marginMode = 'isolated';
            walletBalance = this.safeString (position, 'isolatedWallet');
            collateralString = Precise.stringAdd (walletBalance, unrealizedPnlString);
        } else {
            marginMode = 'cross';
            walletBalance = this.safeString (position, 'crossWalletBalance');
            collateralString = this.safeString (position, 'crossMargin');
        }
        const collateral = this.parseNumber (collateralString);
        let marginRatio: Num = undefined;
        let side: Str = undefined;
        let percentage: Num = undefined;
        let liquidationPriceStringRaw: Str = undefined;
        let liquidationPrice: Num = undefined;
        const contractSize = this.safeValue (market, 'contractSize');
        const contractSizeString = this.numberToString (contractSize);
        if (Precise.stringEquals (notionalString, '0')) {
            entryPrice = undefined;
        } else {
            side = Precise.stringLt (notionalString, '0') ? 'short' : 'long';
            marginRatio = this.parseNumber (Precise.stringDiv (Precise.stringAdd (Precise.stringDiv (maintenanceMarginString, collateralString), '5e-5'), '1', 4));
            percentage = this.parseNumber (Precise.stringMul (Precise.stringDiv (unrealizedPnlString, initialMarginString, 4), '100'));
            if (usdm) {
                // calculate liquidation price
                //
                // liquidationPrice = (walletBalance / (contracts * (±1 + mmp))) + (±entryPrice / (±1 + mmp))
                //
                // mmp = maintenanceMarginPercentage
                // where ± is negative for long and positive for short
                // TODO: calculate liquidation price for coinm contracts
                let onePlusMaintenanceMarginPercentageString: Str = undefined;
                let entryPriceSignString = entryPriceString;
                if (side === 'short') {
                    onePlusMaintenanceMarginPercentageString = Precise.stringAdd ('1', maintenanceMarginPercentageString);
                } else {
                    onePlusMaintenanceMarginPercentageString = Precise.stringAdd ('-1', maintenanceMarginPercentageString);
                    entryPriceSignString = Precise.stringMul ('-1', entryPriceSignString);
                }
                const leftSide = Precise.stringDiv (walletBalance, Precise.stringMul (contractsStringAbs, onePlusMaintenanceMarginPercentageString));
                const rightSide = Precise.stringDiv (entryPriceSignString, onePlusMaintenanceMarginPercentageString);
                liquidationPriceStringRaw = Precise.stringAdd (leftSide, rightSide);
            } else {
                // calculate liquidation price
                //
                // liquidationPrice = (contracts * contractSize(±1 - mmp)) / (±1/entryPrice * contracts * contractSize - walletBalance)
                //
                let onePlusMaintenanceMarginPercentageString: Str = undefined;
                let entryPriceSignString = entryPriceString;
                if (side === 'short') {
                    onePlusMaintenanceMarginPercentageString = Precise.stringSub ('1', maintenanceMarginPercentageString);
                } else {
                    onePlusMaintenanceMarginPercentageString = Precise.stringSub ('-1', maintenanceMarginPercentageString);
                    entryPriceSignString = Precise.stringMul ('-1', entryPriceSignString);
                }
                const size = Precise.stringMul (contractsStringAbs, contractSizeString);
                const leftSide = Precise.stringMul (size, onePlusMaintenanceMarginPercentageString);
                const rightSide = Precise.stringSub (Precise.stringMul (Precise.stringDiv ('1', entryPriceSignString), size), walletBalance);
                liquidationPriceStringRaw = Precise.stringDiv (leftSide, rightSide);
            }
            const pricePrecision = this.precisionFromString (this.safeString (market['precision'], 'price'));
            const pricePrecisionPlusOne = pricePrecision + 1;
            const pricePrecisionPlusOneString = pricePrecisionPlusOne.toString ();
            // round half up
            const rounder = new Precise ('5e-' + pricePrecisionPlusOneString);
            const rounderString = rounder.toString ();
            const liquidationPriceRoundedString = Precise.stringAdd (rounderString, liquidationPriceStringRaw);
            let truncatedLiquidationPrice = Precise.stringDiv (liquidationPriceRoundedString, '1', pricePrecision);
            if (truncatedLiquidationPrice === undefined) {
                throw new ExchangeError (this.id + ' method() missing truncatedLiquidationPrice');
            }
            if (truncatedLiquidationPrice[0] === '-') {
                // user cannot be liquidated
                // since he has more collateral than the size of the position
                truncatedLiquidationPrice = undefined;
            }
            liquidationPrice = this.parseNumber (truncatedLiquidationPrice);
        }
        const positionSide = this.safeString (position, 'positionSide');
        const hedged = positionSide !== 'BOTH';
        return {
            'info': position,
            'id': undefined,
            'symbol': symbol,
            'timestamp': timestamp,
            'datetime': this.iso8601 (timestamp),
            'initialMargin': initialMargin,
            'initialMarginPercentage': this.parseNumber (initialMarginPercentageString),
            'maintenanceMargin': maintenanceMargin,
            'maintenanceMarginPercentage': maintenanceMarginPercentage,
            'entryPrice': entryPrice,
            'notional': notional,
            'leverage': this.parseNumber (leverageString),
            'unrealizedPnl': unrealizedPnl,
            'contracts': contracts,
            'contractSize': contractSize,
            'marginRatio': marginRatio,
            'liquidationPrice': liquidationPrice,
            'markPrice': undefined,
            'collateral': collateral,
            'marginMode': marginMode,
            'side': side,
            'hedged': hedged,
            'percentage': percentage,
        };
    }

    /**
     * @method
     * @name aster#fetchAccountPositions
     * @ignore
     * @description fetch account positions
     https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#position-information-v3-user_data
     * @param {string[]} [symbols] list of unified market symbols
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} data on account positions
     */
    async fetchAccountPositions (symbols: Strings = undefined, params = {}) {
        if (symbols !== undefined) {
            if (!Array.isArray (symbols)) {
                throw new ArgumentsRequired (this.id + ' fetchPositions() requires an array argument for symbols');
            }
        }
        await this.loadMarketsAndSignIn ();
        await this.loadLeverageBrackets (false, params);
        const response = await this.fapiPrivateGetV4Account (params);
        let filterClosed: Bool = undefined;
        [ filterClosed, params ] = this.handleOptionAndParams (params, 'fetchAccountPositions', 'filterClosed', false);
        const result = this.parseAccountPositions (response, filterClosed);
        symbols = this.marketSymbols (symbols);
        return this.filterByArrayPositions (result, 'symbol', symbols, false);
    }

    async loadLeverageBrackets (reload = false, params = {}) {
        await this.loadMarketsAndSignIn ();
        // by default cache the leverage bracket
        // it contains useful stuff like the maintenance margin and initial margin for positions
        const leverageBrackets = this.safeDict (this.options, 'leverageBrackets');
        if ((leverageBrackets === undefined) || (reload)) {
            const response = await this.fapiPrivateGetV3LeverageBracket (params);
            //
            //    [
            //        {
            //            "symbol": "TRUTHUSDT",
            //            "brackets": [
            //                {
            //                    "bracket": "1",
            //                    "initialLeverage": "50",
            //                    "notionalCap": "5000",
            //                    "notionalFloor": "0",
            //                    "maintMarginRatio": "0.01",
            //                    "cum": "0.0"
            //                },
            //                {
            //                    "bracket": "2",
            //                    "initialLeverage": "20",
            //                    "notionalCap": "10000",
            //                    "notionalFloor": "5000",
            //                    "maintMarginRatio": "0.025",
            //                    "cum": "75.0"
            //                },
            //                ...
            //
            this.options['leverageBrackets'] = this.createSafeDictionary ();
            const entries = this.toArray (response);
            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i];
                const marketId = this.safeString (entry, 'symbol');
                const symbol = this.safeSymbol (marketId, undefined, undefined, 'contract');
                const brackets = this.safeList (entry, 'brackets', []);
                const result: List = [];
                for (let j = 0; j < brackets.length; j++) {
                    const bracket = brackets[j];
                    const floorValue = this.safeString (bracket, 'notionalFloor');
                    const maintenanceMarginPercentage = this.safeString (bracket, 'maintMarginRatio');
                    result.push ([ floorValue, maintenanceMarginPercentage ]);
                }
                this.options['leverageBrackets'][symbol] = result;
            }
        }
        return this.options['leverageBrackets'];
    }

    keccakMessage (message: any) {
        return '0x' + this.hash (message, keccak, 'hex');
    }

    signMessage (message: any, privateKey: any) {
        return this.signHash (this.keccakMessage (message), privateKey.slice (-64));
    }

    signWithdrawPayload (withdrawPayload: any, network: any): string {
        const chainId = this.safeInteger (withdrawPayload, 'chainId');
        const domain: Dict = {
            'chainId': chainId,
            'name': 'Aster',
            'verifyingContract': this.safeString (this.options, 'zeroAddress'),
            'version': '1',
        };
        const messageTypes: Dict = {
            'Action': [
                { 'name': 'type', 'type': 'string' },
                { 'name': 'destination', 'type': 'address' },
                { 'name': 'destination Chain', 'type': 'string' },
                { 'name': 'token', 'type': 'string' },
                { 'name': 'amount', 'type': 'string' },
                { 'name': 'fee', 'type': 'string' },
                { 'name': 'nonce', 'type': 'uint256' },
                { 'name': 'aster chain', 'type': 'string' },
            ],
        };
        const request = {
            'type': 'Withdraw',
            'destination': this.safeString (withdrawPayload, 'receiver'),
            'destination Chain': network,
            'token': this.safeString (withdrawPayload, 'asset'),
            'amount': this.safeString (withdrawPayload, 'amount'),
            'fee': this.safeString (withdrawPayload, 'fee'),
            'nonce': this.safeInteger (withdrawPayload, 'userNonce'),
            'aster chain': 'Mainnet',
        };
        const msg = this.ethEncodeStructuredData (domain, messageTypes, request);
        const signature = this.signMessage (msg, this.privateKey);
        return signature;
    }

    /**
     * @method
     * @name aster#withdraw
     * @description make a withdrawal
     * @see https://asterdex.github.io/aster-api-website/spot-v3/account%26trades/#withdraw-user_data
     * @see https://asterdex.github.io/aster-api-website/futures-v3/deposit%26withdrawal/#withdraw-by-fapiv3-evm-futures
     * @see https://asterdex.github.io/aster-api-website/futures-v3/deposit%26withdrawal/#withdraw-by-fapiv3-evm-spot
     * @param {string} code unified currency code
     * @param {float} amount the amount to withdraw
     * @param {string} address the address to withdraw to
     * @param {string} tag
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [transaction structure]{@link https://docs.ccxt.com/?id=transaction-structure}
     */
    override async withdraw (code: string, amount: number, address: string, tag: Str = undefined, params = {}): Promise<Transaction> {
        [ tag, params ] = this.handleWithdrawTagAndParams (tag, params);
        this.checkAddress (address);
        await this.loadMarketsAndSignIn ();
        const currency = this.currency (code);
        const nonce = this.milliseconds () * 1000;
        const request: Dict = {
            'asset': currency['id'],
            'receiver': address,
            'userNonce': nonce.toString (),
        };
        let chainId = this.safeInteger (params, 'chainId');
        // TODO: check how ARBI signature would work
        const networks = this.safeDict (this.options, 'networks', {});
        let network = this.safeStringUpper (params, 'network');
        network = this.safeString (networks, network, network);
        if ((chainId === undefined) && (network !== undefined)) {
            const chainIds = this.safeDict (this.options, 'networksToChainId', {});
            chainId = this.safeInteger (chainIds, network);
        }
        if (chainId === undefined) {
            throw new ArgumentsRequired (this.id + ' withdraw require chainId or network parameter');
        }
        request['chainId'] = chainId;
        const fee = this.safeString (params, 'fee');
        if (fee === undefined) {
            throw new ArgumentsRequired (this.id + ' withdraw require fee parameter');
        }
        request['fee'] = fee;
        params = this.omit (params, [ 'chainId', 'network', 'fee' ]);
        request['amount'] = this.currencyToPrecision (code, amount, network);
        request['userSignature'] = this.signWithdrawPayload (request, network);
        const response = await this.sapiPrivatePostV3AsterUserWithdraw (this.extend (request, params));
        //
        //   {
        //       "withdrawId": "1097219372504338432",
        //       "hash": "0x9e6baa3eb75d92a1164eef51a0cc97b9591930518ba3e8e5ab40ce524ba4e463"
        //   }
        //
        return this.parseTransaction (response, currency);
    }

    override parseTransaction (transaction: any, currency: Currency = undefined): Transaction {
        return {
            'info': transaction,
            'id': this.safeString (transaction, 'withdrawId'),
            'txid': this.safeString (transaction, 'hash'),
            'timestamp': undefined,
            'datetime': undefined,
            'network': undefined,
            'address': undefined,
            'addressTo': undefined,
            'addressFrom': undefined,
            'tag': undefined,
            'tagTo': undefined,
            'tagFrom': undefined,
            'type': 'withdrawal',
            'amount': undefined,
            'currency': undefined,
            'status': undefined,
            'updated': undefined,
            'internal': undefined,
            'comment': undefined,
            'fee': undefined,
        };
    }

    /**
     * @method
     * @name aster#transfer
     * @description transfer currency internally between wallets on the same account
     * @see https://asterdex.github.io/aster-api-website/spot-v3/account%26trades/#perp-spot-transfer-trade
     * @see https://asterdex.github.io/aster-api-website/futures-v3/account%26trades/#transfer-between-futures-and-spot-transfer
     * @param {string} code unified currency code
     * @param {float} amount amount to transfer
     * @param {string} fromAccount account to transfer from
     * @param {string} toAccount account to transfer to
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns {object} a [transfer structure]{@link https://docs.ccxt.com/?id=transfer-structure}
     */
    override async transfer (code: string, amount: number, fromAccount: string, toAccount:string, params = {}): Promise<TransferEntry> {
        await this.loadMarketsAndSignIn ();
        const currency = this.currency (code);
        const request: Dict = {
            'asset': currency['id'],
            'amount': this.currencyToPrecision (code, amount),
        };
        let type: Str = undefined;
        let fromId: Str = undefined;
        if (fromAccount !== undefined) {
            fromId = this.convertTypeToAccount (fromAccount).toUpperCase ();
        }
        let toId: Str = undefined;
        if (toAccount !== undefined) {
            toId = this.convertTypeToAccount (toAccount).toUpperCase ();
        }
        if (fromId === 'SPOT' && toId === 'FUTURE') {
            type = 'SPOT_FUTURE';
        } else if (fromId === 'FUTURE' && toId === 'SPOT') {
            type = 'FUTURE_SPOT';
        }
        if (type === undefined) {
            throw new ArgumentsRequired (this.id + ' transfer() requires fromAccount and toAccount parameters to be either SPOT or FUTURE');
        }
        const defaultClientTranId = this.numberToString (this.milliseconds ());
        const clientTranId = this.safeString (params, 'clientTranId', defaultClientTranId);
        request['kindType'] = type;
        request['clientTranId'] = clientTranId;
        const response = await this.sapiPrivatePostV3AssetWalletTransfer (this.extend (request, params));
        return this.parseTransfer (response, currency);
    }

    override parseTransfer (transfer: Dict, currency: Currency = undefined): TransferEntry {
        const currencyId = this.safeString (transfer, 'code');
        return {
            'info': transfer,
            'id': this.safeString (transfer, 'tranId'),
            'timestamp': undefined,
            'datetime': undefined,
            'currency': this.safeCurrencyCode (currencyId, currency),
            'amount': undefined,
            'fromAccount': undefined,
            'toAccount': undefined,
            'status': this.parseTransferStatus (this.safeString (transfer, 'status')),
        };
    }

    parseTransferStatus (status: Str): Str {
        const statuses: Dict = {
            'SUCCESS': 'ok',
        };
        return this.safeString (statuses, (status as string), status);
    }

    hashMessage (binaryMessage: any) {
        // const binaryMessage = this.encode (message);
        const binaryMessageLength = this.binaryLength (binaryMessage);
        const x19 = this.base16ToBinary ('19');
        const newline = this.base16ToBinary ('0a');
        const prefix = this.binaryConcat (x19, this.encode ('Ethereum Signed Message:'), newline, this.encode (this.numberToString (binaryMessageLength)));
        return '0x' + this.hash (this.binaryConcat (prefix, binaryMessage), keccak, 'hex');
    }

    signHash (hash: any, privateKey: any) {
        this.checkRequiredCredentials ();
        const signature = ecdsa (hash.slice (-64), privateKey.slice (-64), secp256k1, undefined);
        const r = signature['r'];
        const s = signature['s'];
        const v = this.intToBase16 (this.sum (27, signature['v']));
        return '0x' + r.padStart (64, '0') + s.padStart (64, '0') + v;
    }

    override sign (path: any, api: any = 'public', method = 'GET', params = {}, headers: NullableDict = undefined, body: any = undefined) {
        let url = this.urls['api'][api] + '/' + path;
        if (api === 'fapiPublic' || api === 'sapiPublic') {
            if (Object.keys (params).length) {
                url += '?' + this.rawencode (params);
            }
        } else if (api === 'fapiPrivate' || api === 'sapiPrivate') {
            this.checkRequiredCredentials ();
            const nonce = this.milliseconds () * 1000;
            // Sign using EIP-712 typed data per the AsterSignTransaction spec
            const zeroAddress = this.safeString (this.options, 'zeroAddress', '0x0000000000000000000000000000000000000000');
            const v3ChainId = this.safeInteger (this.options, 'v3ChainId', 1666);
            let walletAddress = this.safeString (this.options, 'cachedWalletAddress');
            const privateKeyHash = this.hash (this.encode (this.privateKey), keccak, 'hex');
            const cachedPrivateKeyHash = this.safeString (this.options, 'privateKeyHashForCachedWalletAddress');
            if ((walletAddress === undefined) || (cachedPrivateKeyHash !== privateKeyHash)) {
                walletAddress = this.ethGetAddressFromPrivateKey (this.privateKey);
                this.options['cachedWalletAddress'] = walletAddress;
                this.options['privateKeyHashForCachedWalletAddress'] = privateKeyHash;
            }
            const signerAddress = this.safeString (this.options, 'signerAddress', walletAddress); // default to user's wallet
            if (signerAddress === undefined) {
                throw new ArgumentsRequired (this.id + ' requires signerAddress in options when use v3 api');
            }
            const domain = {
                'name': 'AsterSignTransaction',
                'version': '1',
                'chainId': v3ChainId,
                'verifyingContract': zeroAddress,
            };
            let messageTypes: Dict = {
                'Message': [
                    { 'name': 'msg', 'type': 'string' },
                ],
            };
            // Build v3 params: original endpoint params + nonce (microseconds) + user + signer
            // Note: timestamp and recvWindow are not used for v3; nonce replaces timestamp
            const finalParams = this.extend ({
                'nonce': nonce.toString (),
                'user': walletAddress,
                'signer': signerAddress,
            }, params);
            let paramString: Str = undefined;
            let paramsToEncode: Dict;
            const isApproveBuilder = (path.indexOf ('/approveBuilder') >= 0);
            if (isApproveBuilder) {
                // domain['name'] = 'Aster';
                messageTypes = {
                    'ApproveBuilder': [
                        { 'name': 'Builder', 'type': 'string' },
                        { 'name': 'MaxFeeRate', 'type': 'string' },
                        { 'name': 'BuilderName', 'type': 'string' },
                        { 'name': 'AsterChain', 'type': 'string' },
                        { 'name': 'User', 'type': 'string' },
                        { 'name': 'Nonce', 'type': 'uint256' },
                    ],
                };
                delete finalParams['signer']; // signer is not needed for approveBuilder endpoint
                paramString = this.encodeValuesWithJson (finalParams);
                paramsToEncode = this.capitalizeKeys (finalParams);
            } else {
                paramString = this.encodeValuesWithJson (finalParams);
                paramsToEncode = { 'msg': paramString };
            }
            const encodedMessage = this.ethEncodeStructuredData (domain, messageTypes, paramsToEncode);
            const signature = this.signMessage (encodedMessage, this.privateKey);
            const queryString = paramString + '&' + 'signature=' + signature;
            if (method === 'GET') {
                url += '?' + queryString;
            } else {
                headers = {};
                headers['Content-Type'] = 'application/x-www-form-urlencoded';
                body = queryString;
            }
        }
        return { 'url': url, 'method': method, 'body': body, 'headers': headers };
    }

    encodeValuesWithJson (values: Dict): string {
        let encodedString = '';
        const keys = Object.keys (values);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const value = values[key];
            const isObj = Array.isArray (value) || this.isDictionary (value);
            const valueJsonified = isObj ? this.json (value) : value.toString ();
            const encoded = this.encodeURIComponent (valueJsonified);
            encodedString += key + '=' + encoded + '&';
        }
        return encodedString.slice (0, -1);
    }

    capitalizeKeys (dict: Dict): Dict {
        const capitalized: Dict = {};
        const keys = Object.keys (dict);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const value = dict[key];
            const capitalizedKey = this.capitalize (key);
            capitalized[capitalizedKey] = value;
        }
        return capitalized;
    }

    override async loadMarketsAndSignIn () {
        await Promise.all ([ this.loadMarkets (), this.signIn () ]);
    }

    /**
     * @method
     * @name aster#signIn
     * @description sign in, must be called prior to using other authenticated methods
     * @see https://asterdex.github.io/aster-api-website/asterCode/integration-flow/
     * @param {object} [params] extra parameters specific to the exchange API endpoint
     * @returns response from exchange
     */
    override async signIn (params = {}) {
        if (this.isEmptyString (this.privateKey)) {
            if (!this.isEmptyString (this.apiKey) || !this.isEmptyString (this.secret)) {
                throw new NotSupported (this.id + 'after the latest upgrade (v4.5.52), CCXT now expects the l1 private key to be provided in the credentials.');
            }
            return false;
        }
        if (this.privateKey.length > 66) {
            throw new NotSupported (this.id + ' after the latest update (v4.5.52), CCXT now expects the l1 private key to be provided in the credentials.');
        }
        await this.initializeClient (params);
        return true;
    }

    async initializeClient (params = {}) {
        const builderFee = this.safeBool (params, 'builderFee', this.safeBool (this.options, 'builderFee', true)); // we shouldn't omit here
        if (!builderFee) {
            return false; // skip if builder fee is not enabled
        }
        const approvedBuilderFee = this.safeBool (this.options, 'approvedBuilderFee', false);
        if (approvedBuilderFee) {
            return true; // skip if builder fee is already approved
        }
        const result = await this.fapiPrivateGetV3Builder ();
        //
        //    [
        //        {
        //            "userAddress": "0x35a5B33Be664B09F78b5089eb6185f71c8a7f11f",
        //            "builderAddress": "0x1F5877C19e3777Cfd15F9d57253eA4aA5254Ec39",
        //            "maxFeeRate": "0.001",
        //            "builderName": "ccxt"
        //        }
        //    ]
        //
        const approvedBuilders = result;
        const length = approvedBuilders.length;
        let found = false;
        for (let i = 0; i < length; i++) {
            const builderInfo = this.safeDict (approvedBuilders, i, {});
            const builderAccountId = this.safeString (builderInfo, 'builderAddress');
            if (builderAccountId === this.safeString (this.options, 'builder')) {
                found = true;
                break;
            }
        }
        if (!found) {
            this.options['approvedBuilderFee'] = true;
            try {
                const request: Dict = {
                    'builder': this.safeString (this.options, 'builder'),
                    'builderName': this.safeString (this.options, 'builderName', 'ccxt'),
                    'maxFeeRate': this.safeString (this.options, 'builderRate'),
                    'signatureChainId': this.safeInteger (this.options, 'v3ChainId', 1666),
                    'asterChain': 'Mainnet',
                };
                const authResponse = await this.fapiPrivatePostV3ApproveBuilder (this.extend (request, params));
                //
                // {"code": 200,"msg": "success"}
                //
                const codeRes = this.safeInteger (authResponse, 'code');
                if (codeRes !== 200) {
                    throw new ExchangeError ('Builder authorization failed, ' + this.json (authResponse));
                }
            } catch (e) {
                this.options['approvedBuilderFee'] = false;
                this.options['builderFee'] = false; // disable if err
            }
        }
        return undefined; // just c#
    }

    override handleErrors (httpCode: int, reason: string, url: string, method: string, headers: Dict, body: string, response: any, requestHeaders: any, requestBody: any) {
        if (response === undefined) {
            return undefined; // fallback to default error handler
        }
        //
        //    {
        //        "code": -1121,
        //        "msg": "Invalid symbol.",
        //    }
        //
        const code = this.safeString (response, 'code');
        const message = this.safeString (response, 'msg');
        if (code !== undefined && code !== '200') {
            const feedback = this.id + ' ' + body;
            this.throwExactlyMatchedException (this.exceptions['exact'], message, feedback);
            this.throwExactlyMatchedException (this.exceptions['exact'], code, feedback);
            this.throwBroadlyMatchedException (this.exceptions['broad'], message, feedback);
            throw new ExchangeError (feedback); // unknown message
        }
        return undefined;
    }
}
